import Phaser from 'phaser';
import { Capacitor } from '@capacitor/core';
import SocketManager from '../network/SocketManager';
import type { RoomState, LeaderboardEntry, JoinResponse, OnlineStats } from '@shared/types';
import { MAX_PLAYERS } from '@shared/game-constants';
import { getPriceConfigByCountryCode } from '@shared/pricing';
import { normalizeRoomCode, parseAppRoute, setAppRoute } from '../router';
import { playSfx, SFX_TAP_KEY } from '../audio/sfx';
import { detectCountryCode, cacheCountryCode, getCachedCountryCode } from '../geo/CountryDetection';
import AuthManager, { AccessState, AuthAccount, AccountProfile } from '../auth/AuthManager';
import { API_BASE_URL, CLIENT_APP_URL, NATIVE_BILLING_REDIRECT_URL, NATIVE_BILLING_RETURN_URL } from '../config';
import {
  applySelectedTheme,
  getFeatureStateView,
  purchaseItemOnServer,
  syncWalletFromServer,
  equipIconOnServer,
  setSelectedTheme,
  type PickitupTheme,
} from '../features/FeatureState';
import { getChallengesForWeek, getCurrentWeekKey, type WeeklyChallenge } from '../data/challenges';
import starImgUrl from '../../assets/star.png';
import moonImgUrl from '../../assets/moon.png';
import heartImgUrl from '../../assets/heart.png';
import hufeImgUrl from '../../assets/hufe.png';
import kleeblattImgUrl from '../../assets/kleeblatt.png';
import kroneImgUrl from '../../assets/krone.png';

type LobbyView = 'main' | 'play' | 'profile' | 'leaderboard' | 'settings' | 'roadmap' | 'challenges' | 'room';
type SettingKey = 'sound';

const DEFAULT_SETTINGS: Record<SettingKey, boolean> = {
  sound: true,
};

const SETTING_LABELS: Record<SettingKey, { title: string; subtitle: string }> = {
  sound: { title: 'Soundeffekte', subtitle: 'Klicks, Treffer, Walzen und UI-Sounds' },
};

const STANDARD_ICON_ITEMS = [
  { id: 'standard:STAR', symbol: 'STAR', title: 'Original Stern', description: 'Standard-Icon', preview: starImgUrl },
  { id: 'standard:MOON', symbol: 'MOON', title: 'Original Mond', description: 'Standard-Icon', preview: moonImgUrl },
  { id: 'standard:HORSESHOE', symbol: 'HORSESHOE', title: 'Original Hufeisen', description: 'Standard-Icon', preview: hufeImgUrl },
  { id: 'standard:HEART', symbol: 'HEART', title: 'Original Herz', description: 'Standard-Icon', preview: heartImgUrl },
  { id: 'standard:CROWN', symbol: 'CROWN', title: 'Original Krone', description: 'Standard-Icon', preview: kroneImgUrl },
  { id: 'standard:CLOVER', symbol: 'CLOVER', title: 'Original Kleeblatt', description: 'Standard-Icon', preview: kleeblattImgUrl },
];

export class LobbyScene extends Phaser.Scene {
  private static readonly PRIVACY_POLICY_URL = 'https://devnova.de/projekte/pickitup/privacy/';
  private sm!: SocketManager;
  private auth!: AuthManager;
  private activeView: LobbyView = 'main';
  private inRoom = false;
  private leaderboardEntries: LeaderboardEntry[] = [];
  private settingsState: Record<SettingKey, boolean> = { ...DEFAULT_SETTINGS };
  private authAccount: AuthAccount | null = null;
  private access: AccessState | null = null;
  private profileData: AccountProfile | null = null;
  private onlineStats: OnlineStats | null = null;
  private onlineStatsPollTimer: number | null = null;
  private authLoading = false;
  private oauthInFlight = false;
  private premiumStorePrice: string | null = null;
  private premiumPriceLookupInFlight: Promise<void> | null = null;
  private nativeBillingListenerReady = false;
  private nativeBillingAppUrlListener: { remove: () => Promise<void> } | null = null;
  private nativeBillingBrowserFinishedListener: { remove: () => Promise<void> } | null = null;
  private lastHandledNativeBillingUrl = '';
  private static readonly PENDING_BILLING_KEY = 'pickitup_pending_billing';
  private countryCode: string | null = null;
  private weeklyChallenges: WeeklyChallenge[] | null = null;
  private weeklyChallengeWeekKey: string | null = null;
  private postLoginTarget: 'shop' | 'challenges' | null = null;
  private readonly popStateHandler = () => this.applyRouteFromUrl();

  constructor() {
    super({ key: 'LobbyScene' });
  }

  create(): void {
    this.sm = SocketManager.getInstance();
    this.auth = AuthManager.getInstance();
    const socket = this.sm.getSocket();
    this.consumeAuthTokenFromUrl();
    this.sm.setAuthToken(this.auth.getToken());

    this.inRoom = Boolean(this.sm.playerId && this.sm.roomId);
    this.loadSettings();

    // Initialize country code for pricing
    void this.initializeCountryCode();

    this.buildHTML();
    this.attachEvents();
    this.renderPremiumPricingCopy();
    void this.ensureNativePremiumPriceLoaded();
    void this.setupNativeBillingHandlers();

    this.applyRouteFromUrl(true);

    socket.on('ROOM_UPDATE', (data) => this.onRoomUpdate(data.room));
    socket.on('GAME_START', (data) => this.onGameStart(data.room));
    socket.on('LEADERBOARD', (data) => this.onLeaderboard(data.entries));
    socket.on('ONLINE_STATS', (data) => this.onOnlineStats(data));
    socket.on('ERROR', (data) => this.showError(data.message));
    // Listen for local session-lost notifications dispatched by SocketManager
    window.addEventListener('pickitup:session-lost', (e: Event) => {
      const detail = (e as CustomEvent).detail;
      this.onSessionLost(detail?.error);
    });
    this.startOnlineStatsPolling();

    window.addEventListener('popstate', this.popStateHandler);
    this.events.once('shutdown', this.cleanup, this);
    void this.refreshAuthState();
  }

  private async initializeCountryCode(): Promise<void> {
    // Try to get cached country code first
    const cached = getCachedCountryCode();
    if (cached) {
      this.countryCode = cached;
      return;
    }

    // Otherwise detect it
    try {
      const result = await detectCountryCode();
      this.countryCode = result.countryCode;
      if (result.countryCode) {
        cacheCountryCode(result.countryCode);
      }
    } catch {
      // Fallback to Germany on error
      this.countryCode = 'de';
    }
  }

  private buildHTML(): void {
    const uiLayer = document.getElementById('ui-layer');
    if (!uiLayer) return;

    uiLayer.innerHTML = `
      <div class="ui-bg-glow"></div>
      <div class="ui-bg-glow-2"></div>
      
      <!-- MAIN MENU -->
      <div id="view-main" class="ui-panel" style="display: none;">
        <h1 class="ui-title">PICKITUP</h1>
        <p class="ui-subtitle">Starte direkt im Einzelspieler oder geh online mit deinem Account.</p>

        <button id="btn-singleplayer-home" class="ui-btn ui-btn-success">Spielen</button>
        <button id="btn-online-home" class="ui-btn ui-btn-primary">Online spielen</button>
        <div class="ui-row">
          <div class="ui-col">
            <button id="btn-home-shop" class="ui-btn ui-btn-outline">Shop</button>
          </div>
          <div class="ui-col">
            <button id="btn-home-challenges" class="ui-btn ui-btn-outline">Challenges</button>
          </div>
        </div>


        <div class="ui-row">
          <div class="ui-col">
            <button id="btn-leaderboard" class="ui-btn ui-btn-outline">Leaderboard</button>
          </div>
          <div class="ui-col">
            <button id="btn-settings" class="ui-btn ui-btn-outline">Einstellungen</button>
          </div>
        </div>
        <button id="btn-profile" class="ui-btn ui-btn-outline">Mein Profil</button>


        <p id="home-status" class="ui-status"></p>
      </div>

      <!-- PLAY MENU -->
      <div id="view-play" class="ui-panel" style="display: none; width: 640px;">
        <button id="btn-play-back" class="ui-back-pill" aria-label="Zurück">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Zurück</span>
        </button>
        <div class="ui-panel-headline">
          <h1 class="ui-title ui-title-no-margin">ONLINE MULTIPLAYER</h1>
          <div id="multiplayer-online-counter" class="ui-online-counter">Online: 0</div>
        </div>
        <p class="ui-subtitle">Raum erstellen, Code eingeben oder offenem Raum beitreten.</p>

        <div class="ui-auth-card">
          <div class="ui-auth-card-head">
            <div class="ui-auth-user-wrap">
              <span id="multiplayer-auth-dot" class="ui-auth-dot ui-auth-dot-offline" aria-hidden="true"></span>
              <div id="multiplayer-auth-line" class="ui-auth-user">Nicht eingeloggt</div>
            </div>
            <div class="ui-auth-actions">
              <button id="btn-mp-auth" class="ui-auth-link-btn" type="button">Anmelden</button>
              <button id="btn-mp-logout" class="ui-auth-link-btn ui-auth-link-logout" type="button" style="display: none;">Logout</button>
            </div>
          </div>
          <div id="multiplayer-access-line" class="ui-auth-access">Multiplayer-Status wird geladen...</div>
        </div>
        <p id="multiplayer-name-hint" class="ui-auth-inline-status" style="display: none;"></p>
        
        <div class="ui-row">
          <div class="ui-col">
            <div id="play-name-group" class="ui-input-group">
              <label class="ui-label">Dein Name</label>
              <input type="text" id="input-name-play" class="ui-input" placeholder="Dein Name" maxlength="20" autocomplete="nickname" />
            </div>
          </div>
          <div class="ui-col">
            <div class="ui-input-group">
              <label class="ui-label">Raumcode</label>
              <input type="text" id="input-room" class="ui-input" placeholder="ABCDEF" maxlength="6" autocomplete="off" style="text-transform: uppercase; letter-spacing: 3px; text-align: center;" />
            </div>
          </div>
        </div>

        <div class="ui-row">
          <div class="ui-col">
            <button id="btn-create-room" class="ui-btn ui-btn-success">Raum erstellen</button>
          </div>
          <div class="ui-col">
            <button id="btn-join-room" class="ui-btn ui-btn-primary">Raum betreten</button>
          </div>
        </div>

        <div class="ui-dailycup-disabled-box">
          <button id="btn-play-daily-cup" class="ui-btn ui-btn-outline" disabled>🏆 Daily Cup – Kommt bald</button>
        </div>
        
        <button id="btn-join-random" class="ui-btn ui-btn-secondary">Zufälligen Raum beitreten</button>
        <p id="play-status" class="ui-status"></p>
      </div>

      <!-- PROFILE -->
      <div id="view-profile" class="ui-panel" style="display: none; width: 560px;">
        <button id="btn-profile-back" class="ui-back-pill" aria-label="Zurück">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Zurück</span>
        </button>
        <h1 class="ui-title">MEIN PROFIL</h1>
        <p class="ui-subtitle">Deine gespeicherten Account-Daten und Datenschutz-Optionen.</p>

        <div class="ui-profile-card">
          <div class="ui-profile-row">
            <span>Benutzername</span>
            <strong id="profile-display-name">-</strong>
          </div>
          <div class="ui-profile-row">
            <span>E-Mail</span>
            <strong id="profile-email">-</strong>
          </div>
          <div class="ui-profile-row">
            <span>Provider</span>
            <strong id="profile-provider">-</strong>
          </div>
          <div class="ui-profile-row">
            <span>Premium</span>
            <strong id="profile-premium">-</strong>
          </div>
          <div class="ui-profile-row">
            <span>Account erstellt</span>
            <strong id="profile-created-at">-</strong>
          </div>
          <div class="ui-profile-row">
            <span>Letzter Login</span>
            <strong id="profile-last-login">-</strong>
          </div>
          <div class="ui-profile-row">
            <span>Premium Quelle</span>
            <strong id="profile-premium-source">-</strong>
          </div>
        </div>

        <p id="profile-status" class="ui-auth-inline-status"></p>
        <button id="btn-profile-refresh" class="ui-btn ui-btn-secondary">Daten aktualisieren</button>
        <button id="btn-profile-delete" class="ui-btn ui-btn-danger">Account löschen</button>
      </div>

      <!-- LEADERBOARD -->
      <div id="view-leaderboard" class="ui-panel" style="display: none; width: 560px;">
        <button id="btn-lb-back" class="ui-back-pill" aria-label="Zurück">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Zurück</span>
        </button>
        <h1 class="ui-title">LEADERBOARD</h1>
        <p class="ui-subtitle">Top 30 Spielerrunden weltweit</p>
        
        <div id="leaderboard-list" class="ui-leaderboard-list">
          <!-- Items injected here -->
        </div>

        <p id="leaderboard-status" class="ui-status">Lade Leaderboard...</p>

        <div class="ui-row">
          <div class="ui-col">
            <button id="btn-lb-refresh" class="ui-btn ui-btn-primary">Aktualisieren</button>
          </div>
        </div>
      </div>

      <!-- SETTINGS -->
      <div id="view-settings" class="ui-panel" style="display: none; width: 520px;">
        <button id="btn-settings-back" class="ui-back-pill" aria-label="Zurück">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Zurück</span>
        </button>
        <h1 class="ui-title">EINSTELLUNGEN</h1>
        <p class="ui-subtitle">Sound</p>
        
        <div id="settings-list" style="width: 100%; margin-bottom: var(--sp-lg);">
          <!-- Settings injected here -->
        </div>

        <button id="btn-settings-privacy" class="ui-btn ui-btn-outline">Datenschutzerklärung</button>
        <p id="settings-hint" class="ui-status" style="margin-bottom: var(--sp-lg);">Einstellungen werden lokal gespeichert.</p>
      </div>


      <!-- SHOP -->
      <div id="view-roadmap" class="ui-panel ui-roadmap-panel ui-shop-panel" style="display: none; width: 900px;">
        <button id="btn-roadmap-back" class="ui-back-pill" aria-label="Zurück">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Zurück</span>
        </button>
        <div class="ui-shop-hero">
          <div class="ui-roadmap-badge">TESTSTAGE SHOP</div>
          <h1 class="ui-title">SHOP</h1>
          <p class="ui-subtitle">Kaufe Themes und eigene PNG-Icons für jeden Symbol-Typ.</p>
          <div class="ui-coin-balance"><span>🪙</span><strong id="shop-coin-balance">0</strong><small>Coins</small></div>
        </div>

        <section class="ui-feature-section">
          <div class="ui-feature-head">
            <div>
              <h2>🎨 Theme Store</h2>
              <p>Modernes Shop-Layout mit kaufbaren Themes.</p>
            </div>
          </div>
          <div id="theme-shop-list" class="ui-shop-grid">
            ${this.renderShopItemsHtml('theme')}
          </div>
        </section>

        <section id="icon-shop-section" class="ui-feature-section ui-icon-shop-section">
          <div class="ui-feature-head">
            <div>
              <h2>🛒 Icons kaufen</h2>
              <p>Standard-Icons und gekaufte Icons direkt unten in das Slot-Popup ziehen.</p>
            </div>
          </div>
          <div id="icon-shop-list" class="ui-shop-grid ui-icon-shop-grid">
            ${this.renderShopItemsHtml('icon')}
          </div>

          <div id="icon-slot-drawer" class="ui-icon-slot-drawer" aria-label="Icon Slots">
            <div id="icon-slot-area" class="ui-icon-slot-area ui-icon-slot-area-drawer">
              ${this.renderIconSlotsHtml()}
            </div>
          </div>
        </section>

        <p id="shop-status" class="ui-status"></p>

      </div>

      <!-- CHALLENGES VIEW -->
      <div id="view-challenges" class="ui-panel ui-roadmap-panel" style="display: none; width: 800px;">
        <button id="btn-challenges-back" class="ui-back-pill" aria-label="Zurück">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Zurück</span>
        </button>
        <h1 class="ui-title">WÖCHENTLICHE CHALLENGES</h1>
        <p class="ui-subtitle">Jede Woche 5 neue Herausforderungen. Erledige sie für Belohnungen!</p>

        <div class="ui-weekly-badge">KW <span id="week-number">--</span></div>

        <div id="challenge-list" class="ui-roadmap-grid">
          ${this.renderWeeklyChallengesHtml()}
        </div>
        <p id="challenges-status" class="ui-status"></p>
      </div>

      <!-- ROOM LOBBY -->
      <div id="view-room" class="ui-panel" style="display: none; width: 760px;">
        <button id="btn-room-back" class="ui-back-pill" aria-label="Zurück">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Zurück</span>
        </button>
        <div class="ui-panel-headline">
          <h1 class="ui-title ui-title-no-margin">LOBBY</h1>
          <div id="room-online-counter" class="ui-online-counter">Online: 0</div>
        </div>
        <div id="room-code-display" class="ui-room-code">CODE: ------</div>
        <p class="ui-subtitle">Teile den Raumcode und warte auf mindestens 2 Spieler (max. ${MAX_PLAYERS}).</p>
        
        <div id="player-slots" style="width: 100%; margin-bottom: var(--sp-md);">
          <!-- Slots injected here -->
        </div>

        <p id="room-status" class="ui-status">0/${MAX_PLAYERS} Spieler</p>
        <button id="btn-start-game" class="ui-btn ui-btn-outline" disabled>WARTE AUF SPIELER...</button>
        <button id="btn-leave-room" class="ui-btn ui-btn-secondary">Raum verlassen</button>
      </div>

      <!-- AUTH MODAL -->
      <div id="modal-auth" class="ui-modal-backdrop" style="display:none;">
        <div class="ui-modal">
          <h2 class="ui-modal-title">Multiplayer Login</h2>
          <p class="ui-modal-subtitle">Bitte melde dich an, um online zu spielen.</p>

          <div class="ui-input-group">
            <label class="ui-label">E-Mail</label>
            <input type="email" id="input-auth-email" class="ui-input" placeholder="name@beispiel.de" autocomplete="email" />
          </div>

          <div class="ui-input-group">
            <label class="ui-label">Passwort</label>
            <input type="password" id="input-auth-password" class="ui-input" placeholder="Mind. 8 Zeichen" autocomplete="current-password" />
          </div>

          <div class="ui-row">
            <div class="ui-col">
              <button id="btn-auth-login" class="ui-btn ui-btn-primary">Login</button>
            </div>
            <div class="ui-col">
              <button id="btn-auth-register" class="ui-btn ui-btn-outline">Registrieren</button>
            </div>
          </div>

          <div class="ui-row">
            <div class="ui-col">
              <button id="btn-auth-google" class="ui-auth-provider-btn ui-auth-provider-google" type="button" aria-label="Mit Google anmelden">
                <span class="ui-auth-provider-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#EA4335" d="M9 3.48c1.69 0 2.84.73 3.49 1.34l2.54-2.48C13.45.9 11.39 0 9 0 5.48 0 2.44 2.02.96 4.96l2.96 2.3C4.63 5.14 6.62 3.48 9 3.48z"/>
                    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.78 2.73v2.26h2.88c1.68-1.55 2.7-3.84 2.7-6.63z"/>
                    <path fill="#FBBC05" d="M3.92 10.74A5.41 5.41 0 0 1 3.62 9c0-.6.1-1.18.3-1.74V5H.96A8.95 8.95 0 0 0 0 9c0 1.45.35 2.82.96 4l2.96-2.26z"/>
                    <path fill="#34A853" d="M9 18c2.39 0 4.39-.79 5.86-2.17l-2.88-2.26c-.8.54-1.82.86-2.98.86-2.38 0-4.37-1.66-5.08-3.88L.96 13.04C2.44 15.98 5.48 18 9 18z"/>
                  </svg>
                </span>
                <span class="ui-auth-provider-text">Mit Google anmelden</span>
              </button>
            </div>
            <div class="ui-col">
              <button id="btn-auth-apple" class="ui-auth-provider-btn ui-auth-provider-apple" type="button" aria-label="Mit Apple anmelden">
                <span class="ui-auth-provider-icon" aria-hidden="true">
                  <svg width="16" height="19" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg">
                    <path fill="currentColor" d="M318.7 268.7c-.2-45.5 37.2-67.3 38.9-68.3-21.2-31.1-54.1-35.4-65.8-35.9-28.1-2.8-54.8 16.5-69 16.5-14.2 0-36.2-16.1-59.6-15.7-30.7 .5-59 17.8-74.8 44.9-31.9 55.3-8.1 137.3 22.9 182.2 15.1 21.8 33.1 46.3 56.8 45.4 22.8-.9 31.3-14.7 58.8-14.7 27.5 0 35.2 14.7 59.2 14.2 24.5-.4 40-22.1 55-44 17.3-25.3 24.4-49.8 24.8-51.1-.5-.2-47.7-18.3-47.9-72.3zM273.2 138c12.6-15.3 21.1-36.5 18.8-57.7-18.2 .7-40.2 12.1-53.2 27.4-11.7 13.6-21.9 35.1-19.1 55.8 20.3 1.6 40.9-10.3 53.5-25.5z"/>
                  </svg>
                </span>
                <span class="ui-auth-provider-text">Mit Apple anmelden</span>
              </button>
            </div>
          </div>

          <p id="auth-inline-status" class="ui-auth-inline-status"></p>
          <button id="btn-auth-close" class="ui-btn ui-btn-outline">Schließen</button>
        </div>
      </div>

      <!-- PREMIUM MODAL -->
      <div id="modal-premium" class="ui-modal-backdrop" style="display:none;">
        <div class="ui-modal">
          <h2 class="ui-modal-title">Premium freischalten</h2>
          <p id="premium-modal-subtitle" class="ui-modal-subtitle"></p>
          <p id="premium-availability-note" class="ui-auth-inline-status"></p>

          <ul class="ui-premium-list">
            <li>Unbegrenzte Online-Räume mit Freunden</li>
            <li>Kein Abo, nur einmal zahlen</li>
            <li>Sofortiger Zugriff nach erfolgreicher Zahlung</li>
          </ul>

          <div class="ui-premium-pay-grid">
            <button id="btn-buy-iap" class="ui-premium-pay-btn ui-premium-pay-btn-iap" type="button">
              <span class="ui-premium-pay-icon ui-premium-pay-icon-iap" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="currentColor" d="M17.1 2.9c.9.9 1.4 2.1 1.4 3.4 0 2.7-2.2 4.9-4.9 4.9s-4.9-2.2-4.9-4.9c0-1.3.5-2.5 1.4-3.4A6.96 6.96 0 0 0 5 9c0 3.9 3.1 7 7 7s7-3.1 7-7c0-2.5-1.3-4.7-3.3-6.1ZM10 19h8a2 2 0 0 1 2 2v1H4v-1a2 2 0 0 1 2-2h2v-2.1c.6.3 1.3.6 2 .8V19Z"/>
                </svg>
              </span>
              <span class="ui-premium-pay-copy">
                <span class="ui-premium-pay-title">In-App-Kauf</span>
                <span id="premium-iap-store" class="ui-premium-pay-sub"></span>
                <span id="premium-iap-price" class="ui-premium-pay-sub">Preis: Storepreis</span>
              </span>
              <span class="ui-premium-pay-arrow" aria-hidden="true">›</span>
            </button>

            <button id="btn-buy-stripe" class="ui-premium-pay-btn ui-premium-pay-btn-card" type="button">
              <span class="ui-premium-pay-icon ui-premium-pay-icon-card" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="5" width="20" height="14" rx="3" fill="currentColor"/>
                  <rect x="2" y="9" width="20" height="3" fill="#FFFFFF" fill-opacity="0.8"/>
                  <rect x="5" y="14.5" width="5.5" height="1.8" rx="0.9" fill="#FFFFFF"/>
                </svg>
              </span>
              <span class="ui-premium-pay-copy">
                <span class="ui-premium-pay-title">Kreditkarte</span>
                <span class="ui-premium-pay-sub">Visa, Mastercard, Amex</span>
              </span>
              <span class="ui-premium-pay-arrow" aria-hidden="true">›</span>
            </button>

            <button id="btn-buy-paypal" class="ui-premium-pay-btn ui-premium-pay-btn-paypal" type="button">
              <span class="ui-premium-pay-icon ui-premium-pay-icon-paypal" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#003087" d="M8.2 4h7.1c2.8 0 4.5 1.8 4 4.4-.5 2.9-2.8 4.6-5.8 4.6H11l-.7 4H6.6L8.2 4Z"/>
                  <path fill="#009CDE" d="M7.1 7.2h6.5c2.4 0 3.7 1.5 3.3 3.5-.4 2.3-2.2 3.7-4.8 3.7H9.9l-.6 3.6H5.8l1.3-10.8Z"/>
                </svg>
              </span>
              <span class="ui-premium-pay-copy">
                <span class="ui-premium-pay-title">PayPal</span>
                <span class="ui-premium-pay-sub">Schneller Checkout</span>
              </span>
              <span class="ui-premium-pay-arrow" aria-hidden="true">›</span>
            </button>
          </div>

          <div id="premium-restore-wrap" class="ui-premium-restore">
            <button id="btn-restore-iap" class="ui-btn ui-btn-outline ui-premium-restore-btn" type="button">
              Käufe wiederherstellen
            </button>
            <p class="ui-premium-restore-copy">
              Schon gekauft? Stelle deinen Kauf über ${this.getInAppPurchaseStoreLabel()} hier wieder her.
            </p>
          </div>

          <p id="premium-inline-status" class="ui-auth-inline-status"></p>
          <button id="btn-premium-close" class="ui-btn ui-btn-outline">Später</button>
        </div>
      </div>
    `;

    // Pre-fill multiplayer name
    if (this.sm.playerName) {
      const namePlayInput = document.getElementById('input-name-play') as HTMLInputElement | null;
      if (namePlayInput) namePlayInput.value = this.sm.playerName;
    }

    this.renderOnlineStats();
    this.renderSettings();
  }

  // Week-based challenges from test DB, fallback to local pool of 100
  private renderWeeklyChallengesHtml(): string {
    const weekKey = getCurrentWeekKey();
    const challenges = this.weeklyChallengeWeekKey === weekKey && this.weeklyChallenges?.length
      ? this.weeklyChallenges
      : getChallengesForWeek(weekKey);

    return challenges.map((item, i) => {
      const target = Math.max(1, item.target);
      const current = Math.max(0, Math.min(target, Math.round(Number(item.progress) || 0)));
      const pct = Math.min(100, Math.round((current / target) * 100));
      const completed = current >= target;
      const claimed = Boolean(item.claimed);
      const claimable = completed && !claimed;
      const stateText = claimed ? 'Erhalten' : claimable ? 'Abholen' : 'Läuft';
      const cardClasses = ['ui-challenge-card'];
      if (completed) cardClasses.push('ui-challenge-complete');
      if (claimed) cardClasses.push('ui-challenge-claimed');
      return `
        <article class="${cardClasses.join(' ')}" data-challenge-id="${this.escapeHtml(item.id)}" data-claimable="${claimable ? 'true' : 'false'}" ${claimable ? 'role="button" tabindex="0" aria-label="Challenge abschließen"' : ''}>
          <div class="ui-challenge-head">
            <span class="ui-challenge-icon">${item.icon || (i + 1)}</span>
            <div class="ui-challenge-info">
              <strong>${this.escapeHtml(item.title)}</strong>
              <p>${this.escapeHtml(item.description)}</p>
            </div>
            <span class="ui-challenge-reward">🪙 ${item.rewardCoins.toLocaleString('de-DE')}</span>
          </div>
          <div class="ui-challenge-progress-row">
            <span class="ui-challenge-progress-label">${current.toLocaleString('de-DE')} / ${target.toLocaleString('de-DE')} ${this.escapeHtml(item.unit)}</span>
            <span class="ui-challenge-progress-pct">${pct}%</span>
          </div>
          <div class="ui-challenge-bar-track">
            <div class="ui-challenge-bar-fill" style="width:${pct}%"></div>
          </div>
          <div class="ui-challenge-state ${claimable ? 'ui-challenge-state-claimable' : ''}">${stateText}</div>
        </article>
      `;
    }).join('');
  }

  private formatChallengeTarget(item: WeeklyChallenge): string {
    return `${item.target.toLocaleString('de-DE')} ${item.unit}`;
  }

  private getSymbolName(sym: string): string {
    const map: Record<string, string> = {
      'STAR': 'Stern', 'MOON': 'Mond', 'HORSESHOE': 'Hufeisen',
      'HEART': 'Herz', 'CROWN': 'Krone', 'CLOVER': 'Kleeblatt'
    };
    return map[sym] || sym;
  }

  private renderIconSlotsHtml(): string {
    const state = getFeatureStateView();
    const symbols = ['STAR', 'MOON', 'HORSESHOE', 'HEART', 'CROWN', 'CLOVER'];
    const iconItems = state.shopItems.filter((item) => !item.id.startsWith('theme'));
    const slots = state.iconSlots as Record<string, string | null>;

    return symbols.map((symKey) => {
      const currentIconId = slots[symKey];
      const currentItem = currentIconId ? iconItems.find((item) => item.id === currentIconId) : null;
      const currentPreview = currentItem?.preview || this.getDefaultSymbolPreview(symKey);
      const title = currentItem?.title || this.getSymbolName(symKey);

      return `
        <article class="ui-symbol-slot" data-symbol="${symKey}" data-drop-target="icon-slot" aria-label="${this.escapeHtml(this.getSymbolName(symKey))}">
          ${this.renderIconPreview(currentPreview, title, 'ui-symbol-slot-preview')}
        </article>
      `;
    }).join('');
  }

  private getDefaultSymbolPreview(symKey: string): string {
    const map: Record<string, string> = {
      STAR: starImgUrl,
      MOON: moonImgUrl,
      HORSESHOE: hufeImgUrl,
      HEART: heartImgUrl,
      CROWN: kroneImgUrl,
      CLOVER: kleeblattImgUrl,
    };
    return map[symKey] || starImgUrl;
  }

  private renderIconPreview(preview: string, title: string, className: string): string {
    const safePreview = this.escapeHtml(preview);
    const safeTitle = this.escapeHtml(title);
    if (preview.startsWith('/pickitup/') || /\.(png|webp|jpe?g|gif)(\?|$)/i.test(preview)) {
      return `<span class="${className}"><img src="${safePreview}" alt="${safeTitle}" loading="lazy" draggable="false" /></span>`;
    }
    return `<span class="${className}">${this.escapeHtml(preview)}</span>`;
  }

  private getThemeCardConfig(id: string): { gradient: string; accentRgb: string } {
    const map: Record<string, { gradient: string; accentRgb: string }> = {
      'theme-classic': { gradient: 'linear-gradient(145deg,#0a0e24 0%,#160d30 55%,#0d1828 100%)', accentRgb: '95,211,255' },
      'theme-royal':   { gradient: 'linear-gradient(145deg,#1c1204 0%,#2e1a00 55%,#3a2208 100%)', accentRgb: '251,191,36'  },
      'theme-neon':    { gradient: 'linear-gradient(145deg,#080c22 0%,#1a0420 55%,#100830 100%)', accentRgb: '34,211,238'  },
      'theme-forest':  { gradient: 'linear-gradient(145deg,#051810 0%,#0a2810 55%,#061e0c 100%)', accentRgb: '52,211,153'  },
      'theme-midnight':{ gradient: 'linear-gradient(145deg,#04091a 0%,#080e2c 55%,#0c0a38 100%)', accentRgb: '147,197,253' },
      'theme-candy':   { gradient: 'linear-gradient(145deg,#220a1e 0%,#2a0830 55%,#1a0a40 100%)', accentRgb: '249,168,212' },
    };
    return map[id] ?? map['theme-classic']!;
  }

  private renderShopItemsHtml(kind: 'theme' | 'icon'): string {
    const state = getFeatureStateView();
    const baseItems = state.shopItems.filter((item) => kind === 'theme' ? item.id.startsWith('theme') : !item.id.startsWith('theme'));
    const items = kind === 'icon'
      ? [
          ...STANDARD_ICON_ITEMS.map(item => ({ ...item, price: 0, owned: true, equipped: false })),
          ...baseItems,
        ]
      : baseItems;

    return items.map((item) => {
      const isStandard = item.id.startsWith('standard:');
      const draggable = kind === 'icon' && (item.owned || isStandard) ? 'true' : 'false';

      if (kind === 'theme') {
        const cfg = this.getThemeCardConfig(item.id);
        const statusLabel = item.equipped ? 'Ausgerüstet' : item.owned ? 'Gekauft' : `${item.price.toLocaleString('de-DE')} Coins`;
        const borderColor = item.equipped
          ? `rgba(251,191,36,0.8)`
          : item.owned
            ? `rgba(${cfg.accentRgb},0.45)`
            : `rgba(${cfg.accentRgb},0.18)`;
        return `
          <button class="ui-shop-card ui-theme-shop-card ui-icon-shop-card ${item.equipped ? 'selected' : ''} ${item.owned ? 'owned' : ''}" data-shop-item-id="${this.escapeHtml(item.id)}" data-shop-kind="theme" data-owned="${item.owned}" type="button" style="background:${cfg.gradient};border-color:${borderColor};">
            ${item.owned ? `<span class="ui-theme-owned-dot"></span>` : ''}
            <strong>${this.escapeHtml(item.title)}</strong>
            <small>${this.escapeHtml(statusLabel)}</small>
          </button>
        `;
      }

      const cta = isStandard
        ? 'Zum Einsetzen ziehen'
        : item.equipped ? 'Ausgerüstet' : item.owned ? 'Zum Einsetzen ziehen' : `${item.price.toLocaleString('de-DE')} Coins`;
      return `
        <button class="ui-shop-card ui-icon-shop-card ${item.equipped ? 'selected' : ''} ${item.owned ? 'owned' : ''} ${isStandard ? 'standard' : ''}" data-shop-item-id="${this.escapeHtml(item.id)}" data-shop-kind="icon" data-owned="${item.owned ? 'true' : 'false'}" draggable="${draggable}" type="button">
          ${this.renderIconPreview(item.preview, item.title, 'ui-shop-preview')}
          <strong>${this.escapeHtml(item.title)}</strong>
          <small>${this.escapeHtml(cta)}</small>
        </button>
      `;
    }).join('');
  }

  private refreshFeatureHub(): void {
    const state = getFeatureStateView();

    const slotList = document.getElementById('icon-slot-area');
    if (slotList) slotList.innerHTML = this.renderIconSlotsHtml();

    const themeList = document.getElementById('theme-shop-list');
    if (themeList) themeList.innerHTML = this.renderShopItemsHtml('theme');

    const iconList = document.getElementById('icon-shop-list');
    if (iconList) iconList.innerHTML = this.renderShopItemsHtml('icon');

    const balance = document.getElementById('shop-coin-balance');
    if (balance) balance.innerText = state.coinBalance.toLocaleString('de-DE');

    this.bindShopButtons();
    this.bindSymbolSlots();
  }

  private bindSymbolSlots(): void {
    document.querySelectorAll<HTMLSelectElement>('#view-roadmap .ui-symbol-slot-select').forEach(select => {
      select.onchange = () => {
        const symbolKey = select.dataset.symbol;
        if (!symbolKey) return;
        void this.equipIconSlot(symbolKey, select.value || null);
      };
    });

    document.querySelectorAll<HTMLElement>('#view-roadmap .ui-symbol-slot[data-drop-target="icon-slot"]').forEach(slot => {
      slot.ondragover = (event) => {
        event.preventDefault();
        slot.classList.add('drag-over');
      };
      slot.ondragleave = () => slot.classList.remove('drag-over');
      slot.ondrop = (event) => {
        event.preventDefault();
        slot.classList.remove('drag-over');
        const symbolKey = slot.dataset.symbol;
        const iconId = event.dataTransfer?.getData('text/plain') || '';
        if (!symbolKey || !iconId) return;
        void this.equipIconSlot(symbolKey, iconId.startsWith('standard:') ? null : iconId);
      };
    });
  }

  private bindShopButtons(): void {
    document.querySelectorAll<HTMLButtonElement>('#view-roadmap .ui-shop-card').forEach((button) => {
      const canDragIcon = () => button.dataset.shopKind === 'icon' && (button.dataset.owned === 'true' || (button.dataset.shopItemId || '').startsWith('standard:'));
      let pointerStart: { x: number; y: number; iconId: string; dragging: boolean } | null = null;
      let dragGhost: HTMLElement | null = null;

      const moveDragGhost = (x: number, y: number) => {
        if (!dragGhost) return;
        dragGhost.style.left = `${x}px`;
        dragGhost.style.top = `${y}px`;
      };

      const ensureDragGhost = (x: number, y: number) => {
        if (dragGhost) {
          moveDragGhost(x, y);
          return;
        }
        const preview = button.querySelector<HTMLElement>('.ui-shop-preview');
        dragGhost = document.createElement('div');
        dragGhost.className = 'ui-drag-icon-ghost';
        dragGhost.innerHTML = preview?.innerHTML || '';
        document.body.appendChild(dragGhost);
        moveDragGhost(x, y);
      };

      const clearPointerDrag = () => {
        pointerStart = null;
        button.classList.remove('is-pointer-dragging');
        dragGhost?.remove();
        dragGhost = null;
        document.querySelectorAll<HTMLElement>('#view-roadmap .ui-symbol-slot.drag-over').forEach(slot => slot.classList.remove('drag-over'));
      };

      const slotAt = (x: number, y: number): HTMLElement | null => {
        const element = document.elementFromPoint(x, y);
        return element?.closest<HTMLElement>('#view-roadmap .ui-symbol-slot[data-drop-target="icon-slot"]') || null;
      };

      button.ondragstart = (event) => {
        if (!canDragIcon()) {
          event.preventDefault();
          return;
        }
        event.dataTransfer?.setData('text/plain', button.dataset.shopItemId || '');
        event.dataTransfer?.setData('application/x-pickitup-icon', button.dataset.shopItemId || '');
        const preview = button.querySelector<HTMLElement>('.ui-shop-preview');
        if (preview && event.dataTransfer?.setDragImage) {
          event.dataTransfer.setDragImage(preview, preview.offsetWidth / 2, preview.offsetHeight / 2);
        }
        event.dataTransfer!.effectAllowed = 'copy';
      };

      button.onpointerdown = (event) => {
        if (!canDragIcon()) return;
        pointerStart = { x: event.clientX, y: event.clientY, iconId: button.dataset.shopItemId || '', dragging: false };
        button.setPointerCapture?.(event.pointerId);
      };

      button.onpointermove = (event) => {
        if (!pointerStart) return;
        const distance = Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y);
        if (distance < 8 && !pointerStart.dragging) return;
        pointerStart.dragging = true;
        button.classList.add('is-pointer-dragging');
        ensureDragGhost(event.clientX, event.clientY);
        document.querySelectorAll<HTMLElement>('#view-roadmap .ui-symbol-slot.drag-over').forEach(slot => slot.classList.remove('drag-over'));
        slotAt(event.clientX, event.clientY)?.classList.add('drag-over');
        event.preventDefault();
      };

      button.onpointerup = (event) => {
        const active = pointerStart;
        if (!active) return;
        const wasDragging = active.dragging;
        const targetSlot = wasDragging ? slotAt(event.clientX, event.clientY) : null;
        clearPointerDrag();
        if (!wasDragging) return;
        button.dataset.justDragged = 'true';
        window.setTimeout(() => { delete button.dataset.justDragged; }, 80);
        event.preventDefault();
        const symbolKey = targetSlot?.dataset.symbol;
        if (!symbolKey || !active.iconId) return;
        void this.equipIconSlot(symbolKey, active.iconId.startsWith('standard:') ? null : active.iconId);
      };

      button.onpointercancel = clearPointerDrag;

      button.onclick = () => {
        if (button.dataset.justDragged === 'true') return;
        const itemId = button.dataset.shopItemId;
        if (!itemId) return;
        playSfx(this, SFX_TAP_KEY, { volume: 0.2 });
        const isOwned = button.dataset.owned === 'true' || itemId.startsWith('standard:');
        const isTheme = button.dataset.shopKind === 'theme';
        if (isTheme) {
          if (isOwned) {
            const themeKey = itemId.startsWith('theme-') ? itemId.slice(6) : itemId;
            setSelectedTheme(themeKey as PickitupTheme);
            this.refreshFeatureHub();
            const status = document.getElementById('shop-status');
            if (status) { status.innerText = 'Theme aktiviert!'; status.style.color = 'var(--accent-green)'; }
          } else {
            void this.purchaseItemFromServer(itemId);
          }
          return;
        }
        if (isOwned) {
          const status = document.getElementById('shop-status');
          if (status) { status.innerText = 'Karte gedrückt halten und auf einen Slot ziehen.'; status.style.color = 'var(--accent-cyan)'; }
          return;
        }
        void this.purchaseItemFromServer(itemId);
      };
    });
  }

  private async equipIconSlot(symbolKey: string, iconId: string | null): Promise<void> {
    const status = document.getElementById('shop-status');
    if (status) {
      status.innerText = iconId ? 'Icon wird ausgerüstet…' : 'Original wird wiederhergestellt…';
      status.style.color = 'var(--accent-cyan)';
    }
    const result = await equipIconOnServer(symbolKey, iconId);
    if (!result.success) {
      if (status) {
        status.innerText = result.message || 'Equip fehlgeschlagen.';
        status.style.color = 'var(--accent-red)';
      }
      return;
    }
    await syncWalletFromServer();
    if (status) {
      status.innerText = iconId ? `${this.getSymbolName(symbolKey)} ersetzt.` : `${this.getSymbolName(symbolKey)} wieder auf Original.`;
      status.style.color = 'var(--accent-green)';
    }
    playSfx(this, SFX_TAP_KEY, { volume: 0.2 });
    this.refreshFeatureHub();
  }

  private async purchaseItemFromServer(itemId: string): Promise<void> {
    const status = document.getElementById('shop-status');
    const setStatus = (msg: string, ok: boolean) => {
      if (!status) return;
      status.innerText = msg;
      status.style.color = ok ? 'var(--accent-green)' : 'var(--accent-red)';
    };
    setStatus('Kaufe...', true);
    const result = await purchaseItemOnServer(itemId);
    setStatus(result.message, result.success);
    if (result.success) {
      await syncWalletFromServer();
    }
    this.refreshFeatureHub();
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private attachEvents(): void {
    const bind = (id: string, fn: () => void) => {
      const el = document.getElementById(id);
      if (!el) return;
      const handler = () => {
        playSfx(this, SFX_TAP_KEY, { volume: 0.2 });
        fn();
      };
      // Support both click events and touch events for iOS compatibility
      el.addEventListener('click', handler);
      el.addEventListener('touchend', (e) => {
        e.preventDefault();
        handler();
      }, { passive: false });
    };

    bind('btn-singleplayer-home', () => this.startOfflineSinglePlayer());
    bind('btn-online-home', () => this.openOnlinePlayFlow());
    bind('btn-leaderboard', () => this.openLeaderboardView());
    bind('btn-settings', () => this.openSettingsView());
    bind('btn-profile', () => {
      void this.openProfileView();
    });
    bind('btn-roadmap', () => { void this.openRoadmapView(); });
    bind('btn-home-shop', () => { void this.openRoadmapView(); });
    bind('btn-home-challenges', () => this.openChallengesView());
    
    bind('btn-create-room', () => this.createRoom());
    bind('btn-join-room', () => this.joinRoom());
    bind('btn-play-daily-cup', () => { void this.openRoadmapView(); });
    bind('btn-join-random', () => this.joinRandomRoom());
    bind('btn-play-back', () => this.openHomeView());
    bind('btn-mp-auth', () => this.openAuthModal());
    bind('btn-mp-logout', () => this.logoutAccount());
    bind('btn-auth-login', () => {
      void this.loginWithEmail();
    });
    bind('btn-auth-register', () => {
      void this.registerWithEmail();
    });
    bind('btn-auth-google', () => {
      void this.loginWithOAuth('google');
    });
    bind('btn-auth-apple', () => {
      void this.loginWithOAuth('apple');
    });
    bind('btn-auth-close', () => this.closeAuthModal());
    bind('btn-buy-stripe', () => {
      void this.buyPremiumStripe();
    });
    bind('btn-buy-paypal', () => {
      void this.buyPremiumPayPal();
    });
    bind('btn-buy-iap', () => {
      void this.buyPremiumInApp();
    });
    bind('btn-restore-iap', () => {
      void this.restorePremiumInApp();
    });
    bind('btn-premium-close', () => this.closePremiumModal());

    bind('btn-lb-refresh', () => this.requestLeaderboard());
    bind('btn-lb-back', () => this.openHomeView());

    bind('btn-settings-privacy', () => this.openPrivacyPolicy());
    bind('btn-settings-back', () => this.openHomeView());
    bind('btn-roadmap-back', () => this.openHomeView());
    bind('btn-challenges-back', () => this.openHomeView());
    bind('btn-profile-refresh', () => {
      void this.refreshProfileView();
    });
    bind('btn-profile-delete', () => {
      void this.deleteMyAccount();
    });
    bind('btn-profile-back', () => this.openHomeView());

    bind('btn-start-game', () => this.startGame());
    bind('btn-room-back', () => this.leaveRoomToHome());
  bind('btn-leave-room', () => this.leaveRoom());

    const bindNameInput = (id: string) => {
      const input = document.getElementById(id) as HTMLInputElement | null;
      if (!input) return;
      const sync = () => {
        const trimmed = input.value.slice(0, 20);
        input.value = trimmed;
        this.sm.setPlayerName(trimmed);
      };
      input.addEventListener('input', sync);
      input.addEventListener('blur', sync);
    };

    bindNameInput('input-name-play');

    const bindBackdropClose = (id: string, closeFn: () => void) => {
      const modal = document.getElementById(id);
      if (!modal) return;
      modal.addEventListener('click', (event) => {
        if (event.target === modal) closeFn();
      });
    };
    bindBackdropClose('modal-auth', () => this.closeAuthModal());
    bindBackdropClose('modal-premium', () => this.closePremiumModal());
  }

  private switchView(view: LobbyView): void {
    this.activeView = view;
    if (typeof document !== 'undefined') {
      document.body.dataset.pickitupView = view;
      const uiLayer = document.getElementById('ui-layer');
      if (uiLayer) uiLayer.scrollTop = 0;
      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
        document.scrollingElement.scrollLeft = 0;
      }
    }
    const views: LobbyView[] = ['main', 'play', 'profile', 'leaderboard', 'settings', 'roadmap', 'challenges', 'room'];
    views.forEach(v => {
      const el = document.getElementById(`view-${v}`);
      if (el) el.style.display = v === view ? 'flex' : 'none';
    });
  }

  private openHomeView(): void {
    this.closeAuthModal();
    this.closePremiumModal();
    setAppRoute({ kind: 'home' });
    this.switchView('main');
  }

  private leaveRoomToHome(): void {
    this.closeAuthModal();
    this.closePremiumModal();
    this.inRoom = false;
    this.sm.logoutAndResetConnection();
    setAppRoute({ kind: 'home' });
    this.switchView('main');

    const roomStatus = document.getElementById('room-status');
    if (roomStatus) {
      roomStatus.innerText = '';
    }
  }

  private openPlayView(): void {
    this.closeAuthModal();
    setAppRoute({ kind: 'play' });
    this.switchView('play');
    const status = document.getElementById('play-status');
    if (status) status.innerText = '';
  }

  private openOnlinePlayFlow(): void {
    if (this.authLoading) {
      const status = document.getElementById('home-status');
      if (status) {
        status.innerText = 'Account wird geladen...';
        status.style.color = 'var(--text-dim)';
      }
      return;
    }

    if (!this.authAccount) {
      this.openAuthModal('Bitte einloggen, um online zu spielen.');
      return;
    }

    this.openPlayView();
  }

  private openSettingsView(): void {
    setAppRoute({ kind: 'settings' });
    this.switchView('settings');
  }

  private async openProfileView(): Promise<void> {
    if (this.authLoading) {
      this.showError('Account wird geladen...');
      return;
    }
    if (!this.authAccount) {
      this.openAuthModal('Bitte einloggen, um dein Profil zu sehen.', true);
      return;
    }
    setAppRoute({ kind: 'profile' });
    this.switchView('profile');
    await this.refreshProfileView();
  }

  private openLeaderboardView(): void {
    setAppRoute({ kind: 'leaderboard' });
    this.switchView('leaderboard');
    this.requestLeaderboard();
  }

  private ensureLoggedInForArea(area: 'shop' | 'challenges'): boolean {
    if (this.authLoading) {
      this.showError('Account wird geladen...');
      return false;
    }
    if (!this.auth.isLoggedIn()) {
      this.postLoginTarget = area;
      const label = area === 'shop' ? 'den Shop' : 'Challenges';
      this.openAuthModal(`Bitte einloggen, um ${label} zu öffnen.`, true);
      return false;
    }
    return true;
  }

  private async syncWalletCoins(): Promise<boolean> {
    const result = await syncWalletFromServer();
    if (result.ok) return true;
    console.warn('[LobbyScene] Wallet sync failed:', result.message);
    if ((result.message || '').toLowerCase().includes('login')) {
      this.auth.logout();
      this.sm.setAuthToken('');
      this.postLoginTarget = 'shop';
      setAppRoute({ kind: 'home' }, { replace: true });
      this.switchView('main');
      this.openAuthModal('Bitte einloggen, um den Shop zu öffnen.', true);
    }
    return false;
  }

  private async openRoadmapView(): Promise<void> {
    if (!this.ensureLoggedInForArea('shop')) return;
    if (!(await this.syncWalletCoins())) return;
    applySelectedTheme();
    setAppRoute({ kind: 'roadmap' });
    this.switchView('roadmap');
    this.refreshFeatureHub();
  }

  private openChallengesView(): void {
    if (!this.ensureLoggedInForArea('challenges')) return;
    applySelectedTheme();
    setAppRoute({ kind: 'challenges' });
    this.switchView('challenges');
    void this.refreshChallengesView();
  }

  private getPostLoginReturnUrl(): string {
    if (this.postLoginTarget === 'shop') return `${window.location.origin}/pickitup/shop`;
    if (this.postLoginTarget === 'challenges') return `${window.location.origin}/pickitup/challenges`;
    return window.location.href;
  }

  private async openPostLoginTarget(): Promise<void> {
    const target = this.postLoginTarget;
    this.postLoginTarget = null;
    if (target === 'shop') {
      await this.openRoadmapView();
      return;
    }
    if (target === 'challenges') {
      this.openChallengesView();
      return;
    }
    this.openPlayView();
  }

  private async refreshChallengesView(): Promise<void> {
    const weekKey = getCurrentWeekKey();
    const weekSpan = document.getElementById('week-number');
    if (weekSpan) weekSpan.innerText = weekKey;

    const list = document.getElementById('challenge-list');
    if (list) list.innerHTML = '<p class="ui-roadmap-muted">Challenges werden geladen…</p>';

    try {
      const token = this.auth.getToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(`${API_BASE_URL}/challenges/current`, { headers });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json() as { success?: boolean; weekKey?: string; challenges?: WeeklyChallenge[] };
      if (payload.success && payload.weekKey && Array.isArray(payload.challenges) && payload.challenges.length > 0) {
        this.weeklyChallengeWeekKey = payload.weekKey;
        this.weeklyChallenges = payload.challenges;
      }
    } catch (err) {
      console.warn('[LobbyScene] Weekly challenges DB fallback:', err);
      this.weeklyChallengeWeekKey = weekKey;
      this.weeklyChallenges = getChallengesForWeek(weekKey);
    }

    const html = this.renderWeeklyChallengesHtml();
    if (list) list.innerHTML = html;
    const weeklyList = document.getElementById('weekly-challenges-list');
    if (weeklyList) weeklyList.innerHTML = html;
    this.bindChallengeClaimHandlers();
  }

  private bindChallengeClaimHandlers(): void {
    document.querySelectorAll<HTMLElement>('.ui-challenge-card[data-claimable="true"]').forEach((card) => {
      const challengeId = card.dataset.challengeId;
      if (!challengeId) return;
      const claim = () => {
        playSfx(this, SFX_TAP_KEY, { volume: 0.2 });
        void this.claimWeeklyChallenge(challengeId);
      };
      card.addEventListener('click', claim);
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        claim();
      });
    });
  }

  private setChallengesStatus(message: string, isError = false): void {
    const status = document.getElementById('challenges-status');
    if (!status) return;
    status.textContent = message;
    status.style.color = isError ? 'var(--accent-red)' : 'var(--accent-green)';
  }

  private async claimWeeklyChallenge(challengeId: string): Promise<void> {
    const token = this.auth.getToken();
    if (!token) {
      this.openAuthModal('Bitte einloggen, um Challenges abzuschließen.');
      return;
    }

    this.setChallengesStatus('Belohnung wird abgeholt…');
    try {
      const response = await fetch(`${API_BASE_URL}/challenges/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ challengeId }),
      });
      const payload = await response.json().catch(() => ({})) as { success?: boolean; message?: string; error?: string; coins?: number };
      if (!response.ok || !payload.success) {
        throw new Error(payload.error || payload.message || `HTTP ${response.status}`);
      }

      await syncWalletFromServer();
      this.setChallengesStatus(payload.message || 'Belohnung erhalten!');
      await this.refreshChallengesView();
    } catch (err) {
      this.setChallengesStatus(err instanceof Error ? err.message : 'Belohnung konnte nicht abgeholt werden.', true);
    }
  }

  private applyRouteFromUrl(initial = false): void {
    const route = parseAppRoute();

    switch (route.kind) {
      case 'home':
        this.switchView('main');
        break;
      case 'play':
        if (this.auth.isLoggedIn()) {
          this.switchView('play');
        } else {
          setAppRoute({ kind: 'home' }, { replace: true });
          this.switchView('main');
          this.openAuthModal('Bitte einloggen, um online zu spielen.');
        }
        break;
      case 'profile':
        if (this.auth.isLoggedIn()) {
          this.switchView('profile');
          void this.refreshProfileView();
        } else {
          setAppRoute({ kind: 'home' }, { replace: true });
          this.switchView('main');
          this.openAuthModal('Bitte einloggen, um dein Profil zu sehen.');
        }
        break;
      case 'leaderboard':
        this.switchView('leaderboard');
        if (initial || this.leaderboardEntries.length === 0) this.requestLeaderboard();
        break;
      case 'settings':
        this.switchView('settings');
        break;
      case 'roadmap':
        if (this.auth.isLoggedIn()) {
          void this.openRoadmapView();
        } else {
          setAppRoute({ kind: 'home' }, { replace: true });
          this.switchView('main');
          this.openAuthModal('Bitte einloggen, um den Shop zu öffnen.', true);
        }
        break;
      case 'challenges':
        if (this.auth.isLoggedIn()) {
          this.switchView('challenges');
          void this.refreshChallengesView();
        } else {
          setAppRoute({ kind: 'home' }, { replace: true });
          this.switchView('main');
          this.openAuthModal('Bitte einloggen, um Challenges zu öffnen.', true);
        }
        break;
      case 'room':
        if (this.inRoom && this.sm.roomId && this.sm.roomId.toUpperCase() === route.roomId) {
          this.switchView('room');
        } else {
          this.switchView('play');
          const roomInput = document.getElementById('input-room') as HTMLInputElement;
          if (roomInput) roomInput.value = route.roomId;
          if (!initial) {
            const status = document.getElementById('play-status');
            if (status) {
              status.innerText = `Raumcode ${route.roomId} geladen.`;
              status.style.color = 'var(--accent-cyan)';
            }
          }
          if (!this.auth.isLoggedIn()) {
            this.openAuthModal('Bitte einloggen, um dem Raum beizutreten.');
          }
        }
        break;
      case 'game':
        if (!this.sm.playerId || !this.sm.roomId) {
          setAppRoute({ kind: 'home' }, { replace: true });
          this.switchView('main');
        } else if (this.inRoom && this.sm.roomId.toUpperCase() === route.roomId) {
          this.switchView('room');
        } else {
          setAppRoute({ kind: 'home' }, { replace: true });
          this.switchView('main');
        }
        break;
      case 'gameOffline':
        this.startOfflineSinglePlayer();
        break;
      case 'result':
        setAppRoute({ kind: 'room', roomId: route.roomId }, { replace: true });
        this.switchView('play');
        const roomInput = document.getElementById('input-room') as HTMLInputElement;
        if (roomInput) roomInput.value = route.roomId;
        const status = document.getElementById('play-status');
        if (status) {
          status.innerText = 'Ergebnis-Link geöffnet. Bitte dem Raum beitreten.';
          status.style.color = 'var(--accent-gold)';
        }
        if (!this.auth.isLoggedIn()) {
          this.openAuthModal('Bitte einloggen, um dem Raum beizutreten.');
        }
        break;
      default:
        setAppRoute({ kind: 'home' }, { replace: true });
        this.switchView('main');
    }
  }

  private requestLeaderboard(): void {
    const status = document.getElementById('leaderboard-status');
    if (status) {
      status.innerText = 'Lade Leaderboard...';
      status.style.color = 'var(--text-dim)';
    }
    this.sm.getSocket().emit('GET_LEADERBOARD');
  }

  private onLeaderboard(entries: LeaderboardEntry[]): void {
    this.leaderboardEntries = [...entries].slice(0, 30);
    this.renderLeaderboard();

    const status = document.getElementById('leaderboard-status');
    if (status) {
      if (this.leaderboardEntries.length === 0) {
        status.innerText = 'Noch keine Einträge vorhanden.';
        status.style.color = 'var(--text-dim)';
      } else {
        status.innerText = `Top ${this.leaderboardEntries.length} Einträge geladen`;
        status.style.color = 'var(--accent-cyan)';
      }
    }
  }

  private onOnlineStats(stats: OnlineStats): void {
    this.onlineStats = {
      onlineTotal: Math.max(0, Math.round(stats.onlineTotal || 0)),
      inRooms: Math.max(0, Math.round(stats.inRooms || 0)),
    };
    this.renderOnlineStats();
  }

  private startOnlineStatsPolling(): void {
    const socket = this.sm.getSocket();
    socket.emit('GET_ONLINE_STATS');

    this.stopOnlineStatsPolling();
    this.onlineStatsPollTimer = window.setInterval(() => {
      socket.emit('GET_ONLINE_STATS');
    }, 3000);
  }

  private stopOnlineStatsPolling(): void {
    if (this.onlineStatsPollTimer == null) return;
    window.clearInterval(this.onlineStatsPollTimer);
    this.onlineStatsPollTimer = null;
  }

  private renderOnlineStats(): void {
    const counters = [
      document.getElementById('multiplayer-online-counter'),
      document.getElementById('room-online-counter'),
    ];
    const label = this.onlineStats
      ? `Online: ${this.onlineStats.onlineTotal}`
      : 'Online: 0';
    counters.forEach((el) => {
      if (!el) return;
      el.textContent = label;
    });
  }

  private renderLeaderboard(): void {
    const list = document.getElementById('leaderboard-list');
    if (!list) return;

    if (this.leaderboardEntries.length === 0) {
      list.innerHTML = '<div class="ui-status">Keine Scores vorhanden</div>';
      return;
    }

    const medals = ['🥇', '🥈', '🥉'];
    list.innerHTML = this.leaderboardEntries.map((entry, i) => {
      const isTop3 = i < 3;
      const rankLabel = isTop3 ? medals[i] : `${i + 1}.`;
      return `
        <div class="ui-lb-item">
          <div class="ui-lb-rank">${rankLabel}</div>
          <div class="ui-lb-name">${entry.name}</div>
          <div class="ui-lb-score">${Math.round(entry.score).toLocaleString('de-DE')}</div>
        </div>
      `;
    }).join('');
  }

  private getNameValue(): string {
    const playInput = document.getElementById('input-name-play') as HTMLInputElement | null;
    const fromPlay = (playInput?.value || '').trim();
    const name = (fromPlay || this.sm.playerName || '').slice(0, 20);

    if (playInput && playInput.value !== name) playInput.value = name;
    this.sm.setPlayerName(name);
    return name;
  }

  private ensureNameValue(fallback = 'Spieler'): string {
    const current = this.getNameValue();
    if (current) return current;
    const value = fallback.slice(0, 20);
    const playInput = document.getElementById('input-name-play') as HTMLInputElement | null;
    if (playInput) playInput.value = value;
    this.sm.setPlayerName(value);
    return value;
  }

  private getMultiplayerName(): string {
    if (this.authAccount?.provider === 'apple') {
      const value = (this.authAccount.displayName || 'Spieler').trim().slice(0, 20) || 'Spieler';
      const playInput = document.getElementById('input-name-play') as HTMLInputElement | null;
      if (playInput) playInput.value = value;
      this.sm.setPlayerName(value);
      return value;
    }
    return this.ensureNameValue(this.authAccount?.displayName || 'Spieler');
  }

  private getRoomValue(): string {
    const el = document.getElementById('input-room') as HTMLInputElement;
    return (el?.value || '').trim().toUpperCase();
  }

  private consumeAuthTokenFromUrl(): void {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const authToken = url.searchParams.get('authToken');
    if (!authToken) return;

    this.auth.setToken(authToken);
    this.sm.setAuthToken(authToken);
    url.searchParams.delete('authToken');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }

  private async refreshAuthState(): Promise<void> {
    this.authLoading = true;
    this.renderAuthState();
    await this.handleBillingReturnFromUrl();
    await this.retryPendingBilling();

    if (!this.auth.isLoggedIn()) {
      this.authAccount = null;
      this.access = null;
      this.profileData = null;
      this.authLoading = false;
      this.renderAuthState();
      if (this.activeView === 'profile') this.openHomeView();
      return;
    }

    try {
      const state = await this.auth.refreshMe();
      this.authAccount = state.account;
      this.access = state.access;
      this.sm.setAuthToken(this.auth.getToken());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login abgelaufen.';
      if (this.shouldInvalidateStoredLogin(message)) {
        this.auth.logout();
        this.sm.setAuthToken('');
        this.authAccount = null;
        this.access = null;
        this.profileData = null;
        this.setAuthInlineStatus(message, true);
        this.setPremiumInlineStatus(message, true);
      } else {
        // Netzwerk-/CORS-/temporäre Serverfehler: Login nicht löschen.
        this.setAuthInlineStatus(`Account konnte nicht geladen werden: ${message}`, true);
        this.setPremiumInlineStatus(`Account konnte nicht geladen werden: ${message}`, true);
      }
    } finally {
      this.authLoading = false;
      this.renderAuthState();
      if (this.activeView === 'profile' && this.authAccount) {
        void this.refreshProfileView();
      }
    }
  }

  private shouldInvalidateStoredLogin(messageRaw: string): boolean {
    const message = String(messageRaw || '').toLowerCase();
    if (!message) return false;
    return (
      /nicht angemeldet/.test(message)
      || /unauthorized/.test(message)
      || /http\s*401/.test(message)
      || /token.*(invalid|ungültig|abgelaufen|expired)/.test(message)
      || /jwt.*(invalid|malformed|expired)/.test(message)
    );
  }

  private getPremiumPriceLabel(): string {
    if (this.premiumStorePrice) return this.premiumStorePrice;
    
    // For web checkout, show the price based on detected country
    if (!this.isInAppPurchaseUiPlatform()) {
      const config = getPriceConfigByCountryCode(this.countryCode);
      return config.displayPrice;
    }
    
    return 'Storepreis';
  }

  private getPremiumAccessLine(): string {
    if (this.access?.freePlay) return 'FreePlay aktiv: Multiplayer gratis';
    if (this.access?.premium) return 'Premium aktiv';
    const price = this.getPremiumPriceLabel();
    return this.isInAppPurchaseUiPlatform()
      ? `Kein Premium aktiv (einmalig ${price})`
      : 'Kein Premium aktiv';
  }

  private getPremiumModalStatusText(): string {
    return `Multiplayer ist Premium (einmalig ${this.getPremiumPriceLabel()}).`;
  }

  private getPremiumModalSubtitleText(): string {
    const price = this.getPremiumPriceLabel();
    return this.isInAppPurchaseUiPlatform()
      ? `Multiplayer kostet einmalig ${price} und bleibt dauerhaft freigeschaltet.`
      : 'Multiplayer wird einmalig freigeschaltet. Den exakten Preis siehst du im Checkout.';
  }

  private renderPremiumPricingCopy(): void {
    const subtitle = document.getElementById('premium-modal-subtitle');
    if (subtitle) subtitle.textContent = this.getPremiumModalSubtitleText();

    const availability = document.getElementById('premium-availability-note');
    if (availability) {
      const countryCode = this.countryCode?.toUpperCase() || 'DE';
      const config = getPriceConfigByCountryCode(this.countryCode);
      
      // Show availability for supported countries
      const supportedCountries = ['DE', 'AT', 'CH', 'FR', 'IT', 'NL', 'BE', 'GB', 'US'];
      if (supportedCountries.includes(countryCode)) {
        availability.textContent = `Premium verfügbar in deinem Land (${this.getPremiumPriceLabel()}).`;
        availability.style.color = 'var(--accent-gold)';
      } else {
        availability.textContent = 'Premium ist in deinem Land nicht verfügbar.';
        availability.style.color = 'var(--accent-gold)';
      }
    }

    const iapStore = document.getElementById('premium-iap-store');
    if (iapStore) {
      iapStore.textContent = this.getInAppPurchaseStoreLabel();
    }

    const iapPrice = document.getElementById('premium-iap-price');
    if (iapPrice) {
      iapPrice.textContent = this.isInAppPurchaseUiPlatform()
        ? `Preis: ${this.getPremiumPriceLabel()}`
        : 'Nur in mobilen Apps verfügbar';
    }
  }

  private async ensureNativePremiumPriceLoaded(forceRefresh = false): Promise<void> {
    if (!this.isNativeMobileApp()) return;
    if (!forceRefresh && this.premiumStorePrice) return;
    if (this.premiumPriceLookupInFlight) {
      await this.premiumPriceLookupInFlight;
      return;
    }

    this.premiumPriceLookupInFlight = (async () => {
      const previous = this.premiumStorePrice;
      try {
        let price: string | null = null;
        const { default: inAppPurchaseManager } = await import('../native/InAppPurchaseManager');
        price = await inAppPurchaseManager.getPremiumDisplayPrice();
        if (price) {
          this.premiumStorePrice = price;
        }
      } catch {
        // Preis bleibt auf Fallback-Text.
      } finally {
        this.renderPremiumPricingCopy();
        if (previous !== this.premiumStorePrice) this.renderAuthState();
      }
    })();

    try {
      await this.premiumPriceLookupInFlight;
    } finally {
      this.premiumPriceLookupInFlight = null;
    }
  }

  private renderAuthState(): void {
    this.renderPremiumPricingCopy();
    this.renderPlayNameState();
    const homeStatus = document.getElementById('home-status');
    const onlineHomeButton = document.getElementById('btn-online-home') as HTMLButtonElement | null;
    const authDot = document.getElementById('multiplayer-auth-dot');
    const userLine = document.getElementById('multiplayer-auth-line');
    const accessLine = document.getElementById('multiplayer-access-line');
    const mpAuthButton = document.getElementById('btn-mp-auth') as HTMLButtonElement | null;
    const mpLogoutButton = document.getElementById('btn-mp-logout') as HTMLButtonElement | null;
    const buyIap = document.getElementById('btn-buy-iap') as HTMLButtonElement | null;
    const restoreIap = document.getElementById('btn-restore-iap') as HTMLButtonElement | null;
    const restoreWrap = document.getElementById('premium-restore-wrap');
    const showIap = this.isInAppPurchaseUiPlatform();
    const buyStripe = document.getElementById('btn-buy-stripe') as HTMLButtonElement | null;
    const buyPayPal = document.getElementById('btn-buy-paypal') as HTMLButtonElement | null;
    const btnCreate = document.getElementById('btn-create-room') as HTMLButtonElement | null;
    const btnJoin = document.getElementById('btn-join-room') as HTMLButtonElement | null;
    const btnJoinRandom = document.getElementById('btn-join-random') as HTMLButtonElement | null;
    if (buyIap) buyIap.style.display = showIap ? '' : 'none';
    if (restoreWrap) restoreWrap.style.display = showIap ? '' : 'none';

    if (this.authLoading) {
      if (userLine) userLine.innerText = 'Lade Account...';
      if (accessLine) {
        accessLine.innerText = 'Status wird aktualisiert...';
        accessLine.style.color = 'var(--text-dim)';
      }
      if (authDot) {
        authDot.classList.remove('ui-auth-dot-online', 'ui-auth-dot-offline');
        authDot.classList.add('ui-auth-dot-loading');
      }
      if (onlineHomeButton) onlineHomeButton.disabled = true;
      if (btnCreate) btnCreate.disabled = true;
      if (btnJoin) btnJoin.disabled = true;
      if (btnJoinRandom) btnJoinRandom.disabled = true;
      if (buyIap) buyIap.disabled = true;
      if (restoreIap) restoreIap.disabled = true;
      if (mpAuthButton) mpAuthButton.disabled = true;
      if (mpLogoutButton) mpLogoutButton.disabled = true;
      return;
    }

    if (onlineHomeButton) onlineHomeButton.disabled = false;
    if (mpAuthButton) mpAuthButton.disabled = false;

    if (!this.authAccount || !this.access) {
      if (userLine) userLine.innerText = 'Nicht eingeloggt';
      if (accessLine) {
        accessLine.innerText = 'Für Multiplayer bitte einloggen.';
        accessLine.style.color = 'var(--accent-red)';
      }
      if (authDot) {
        authDot.classList.remove('ui-auth-dot-online', 'ui-auth-dot-loading');
        authDot.classList.add('ui-auth-dot-offline');
      }
      if (mpAuthButton) {
        mpAuthButton.style.display = 'inline-flex';
        mpAuthButton.innerText = 'Anmelden';
      }
      if (mpLogoutButton) {
        mpLogoutButton.disabled = true;
        mpLogoutButton.style.display = 'none';
      }
      if (btnCreate) btnCreate.disabled = true;
      if (btnJoin) btnJoin.disabled = true;
      if (btnJoinRandom) btnJoinRandom.disabled = true;
      if (buyIap) buyIap.disabled = true;
      if (restoreIap) restoreIap.disabled = true;
      if (buyStripe) buyStripe.disabled = true;
      if (buyPayPal) buyPayPal.disabled = true;
      return;
    }

    if (userLine) {
      userLine.innerText = this.authAccount.displayName;
      userLine.setAttribute('title', this.authAccount.email);
    }
    if (accessLine) {
      const accessText = this.getPremiumAccessLine();
      accessLine.innerText = accessText;
      accessLine.style.color = this.access.premium || this.access.freePlay ? 'var(--accent-green)' : 'var(--accent-gold)';
    }
    if (authDot) {
      authDot.classList.remove('ui-auth-dot-offline', 'ui-auth-dot-loading');
      authDot.classList.add('ui-auth-dot-online');
    }
    if (mpAuthButton) mpAuthButton.style.display = 'none';
    if (mpLogoutButton) {
      mpLogoutButton.disabled = false;
      mpLogoutButton.style.display = 'inline-flex';
    }

    if (buyIap) buyIap.disabled = !showIap || this.access.canPlayMultiplayer;
    if (restoreIap) restoreIap.disabled = !showIap;
    if (buyStripe) buyStripe.disabled = this.access.canPlayMultiplayer;
    if (buyPayPal) buyPayPal.disabled = this.access.canPlayMultiplayer;
    if (btnCreate) btnCreate.disabled = false;
    if (btnJoin) btnJoin.disabled = false;
    if (btnJoinRandom) btnJoinRandom.disabled = false;
    if (homeStatus) homeStatus.innerText = '';
    if (this.access.canPlayMultiplayer) this.closePremiumModal();
  }

  private renderPlayNameState(): void {
    const nameGroup = document.getElementById('play-name-group');
    const nameHint = document.getElementById('multiplayer-name-hint');
    const playInput = document.getElementById('input-name-play') as HTMLInputElement | null;

    if (!nameGroup || !nameHint || !playInput) return;

    if (this.authAccount?.provider === 'apple') {
      const accountName = (this.authAccount.displayName || 'Spieler').trim().slice(0, 20) || 'Spieler';
      playInput.value = accountName;
      this.sm.setPlayerName(accountName);
      nameGroup.style.display = 'none';
      nameHint.style.display = 'block';
      nameHint.innerText = 'Dein Apple-Profilname wird automatisch fuer Multiplayer verwendet.';
      nameHint.style.color = 'var(--text-dim)';
      return;
    }

    nameGroup.style.display = '';
    nameHint.style.display = 'none';
    nameHint.innerText = '';
  }

  private openAuthModal(message?: string, isError = false): void {
    const modal = document.getElementById('modal-auth');
    if (!modal) return;
    if (message) this.setAuthInlineStatus(message, isError);
    modal.style.display = 'flex';
  }

  private closeAuthModal(): void {
    const modal = document.getElementById('modal-auth');
    if (!modal) return;
    modal.style.display = 'none';
  }

  private openPremiumModal(message?: string, isError = false): void {
    if (!this.authAccount) {
      this.openAuthModal('Bitte zuerst einloggen, um Premium zu kaufen.', true);
      return;
    }
    const modal = document.getElementById('modal-premium');
    if (!modal) return;
    this.renderPremiumPricingCopy();
    void this.ensureNativePremiumPriceLoaded();
    if (message) this.setPremiumInlineStatus(message, isError);
    else this.setPremiumInlineStatus(this.getPremiumModalStatusText());
    modal.style.display = 'flex';
  }

  private closePremiumModal(): void {
    const modal = document.getElementById('modal-premium');
    if (!modal) return;
    modal.style.display = 'none';
  }

  private setAuthInlineStatus(message: string, isError = false): void {
    const el = document.getElementById('auth-inline-status');
    if (!el) return;
    el.innerText = message;
    el.style.color = isError ? 'var(--accent-red)' : 'var(--accent-cyan)';
  }

  private setPremiumInlineStatus(message: string, isError = false): void {
    const el = document.getElementById('premium-inline-status');
    if (!el) return;
    el.innerText = message;
    el.style.color = isError ? 'var(--accent-red)' : 'var(--accent-cyan)';
  }

  private async refreshProfileView(): Promise<void> {
    if (!this.authAccount) {
      this.renderProfileData(null);
      this.setProfileStatus('Bitte einloggen, um dein Profil zu sehen.', true);
      return;
    }

    this.renderProfileData(this.profileData);
    this.setProfileStatus('Lade Profildaten...');
    try {
      const profile = await this.auth.getProfile();
      this.profileData = profile;
      this.renderProfileData(profile);
      this.setProfileStatus('Profil geladen.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Profil konnte nicht geladen werden.';
      this.setProfileStatus(message, true);
      if (this.shouldInvalidateStoredLogin(message)) {
        this.logoutAccount();
      }
    }
  }

  private renderProfileData(profile: AccountProfile | null): void {
    const account = profile?.account || this.authAccount;
    const access = profile?.access || this.access;
    const set = (id: string, value: string) => {
      const el = document.getElementById(id);
      if (el) el.textContent = value;
    };

    set('profile-display-name', account?.displayName || '-');
    set('profile-email', account?.email || '-');
    set('profile-provider', this.formatProviderLabel(account?.provider || ''));
    set(
      'profile-premium',
      access?.premium
        ? 'Aktiv'
        : access?.freePlay
          ? 'FreePlay aktiv (temporär)'
          : 'Nicht aktiv',
    );
    set('profile-created-at', this.formatDateTime(profile?.createdAt || null));
    set('profile-last-login', this.formatDateTime(profile?.lastLoginAt || null));
    set('profile-premium-source', profile?.premiumSource || '-');
  }

  private setProfileStatus(message: string, isError = false): void {
    const el = document.getElementById('profile-status');
    if (!el) return;
    el.textContent = message;
    el.style.color = isError ? 'var(--accent-red)' : 'var(--text-dim)';
  }

  private formatProviderLabel(provider: string): string {
    if (provider === 'google') return 'Google';
    if (provider === 'apple') return 'Apple';
    if (provider === 'email') return 'E-Mail';
    return '-';
  }

  private formatDateTime(value: string | null): string {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleString('de-DE');
  }

  private async deleteMyAccount(): Promise<void> {
    if (!this.authAccount) {
      this.openAuthModal('Bitte einloggen, um den Account zu löschen.', true);
      return;
    }

    const confirmation = window.prompt(
      'Zur Bestätigung bitte DELETE oder LÖSCHEN eingeben. Diese Aktion ist endgültig.',
      '',
    );
    if (confirmation === null) return;
    const normalized = confirmation.trim().toUpperCase();
    if (!normalized) {
      this.setProfileStatus('Löschung abgebrochen: keine Bestätigung eingegeben.', true);
      return;
    }

    try {
      await this.auth.deleteAccount(normalized);
      this.sm.logoutAndResetConnection();
      this.authAccount = null;
      this.access = null;
      this.profileData = null;
      this.setProfileStatus('Account wurde gelöscht.');
      this.openHomeView();
    } catch (err) {
      this.setProfileStatus(err instanceof Error ? err.message : 'Account konnte nicht gelöscht werden.', true);
    }
  }

  private getAuthInputs(): { email: string; password: string } {
    const emailInput = document.getElementById('input-auth-email') as HTMLInputElement | null;
    const passwordInput = document.getElementById('input-auth-password') as HTMLInputElement | null;
    return {
      email: (emailInput?.value || '').trim(),
      password: (passwordInput?.value || '').trim(),
    };
  }

  private async loginWithEmail(): Promise<void> {
    const { email, password } = this.getAuthInputs();
    if (!email || !password) {
      this.setAuthInlineStatus('Bitte E-Mail und Passwort eingeben.', true);
      return;
    }
    try {
      this.authLoading = true;
      this.renderAuthState();
      const result = await this.auth.loginEmail(email, password);
      this.authAccount = result.account;
      this.access = result.access;
      this.sm.setAuthToken(result.token);
      this.authLoading = false;
      this.renderAuthState();
      this.setAuthInlineStatus('Erfolgreich eingeloggt.');
      this.closeAuthModal();
      await this.openPostLoginTarget();
    } catch (err) {
      this.setAuthInlineStatus(err instanceof Error ? err.message : 'Login fehlgeschlagen.', true);
    } finally {
      this.authLoading = false;
      this.renderAuthState();
    }
  }

  private async registerWithEmail(): Promise<void> {
    const { email, password } = this.getAuthInputs();
    const displayName = this.ensureNameValue(email.split('@')[0] || 'Player');
    if (!email || !password) {
      this.setAuthInlineStatus('Bitte E-Mail und Passwort eingeben.', true);
      return;
    }
    try {
      this.authLoading = true;
      this.renderAuthState();
      const result = await this.auth.registerEmail(email, password, displayName);
      this.authAccount = result.account;
      this.access = result.access;
      this.sm.setAuthToken(result.token);
      this.authLoading = false;
      this.renderAuthState();
      this.setAuthInlineStatus('Registrierung erfolgreich.');
      this.closeAuthModal();
      await this.openPostLoginTarget();
    } catch (err) {
      this.setAuthInlineStatus(err instanceof Error ? err.message : 'Registrierung fehlgeschlagen.', true);
    } finally {
      this.authLoading = false;
      this.renderAuthState();
    }
  }

  private async loginWithOAuth(provider: 'google' | 'apple'): Promise<void> {
    if (this.oauthInFlight) return;
    try {
      this.oauthInFlight = true;
      this.setAuthInlineStatus(`Öffne ${provider === 'google' ? 'Google' : 'Apple'} Login...`);
      const result = await this.auth.loginWithOAuth(provider, this.getPostLoginReturnUrl());
      this.authAccount = result.account;
      this.access = result.access;
      this.sm.setAuthToken(result.token);
      this.renderAuthState();
      this.setAuthInlineStatus('OAuth Login erfolgreich.');
      this.closeAuthModal();
      await this.openPostLoginTarget();
    } catch (err) {
      this.setAuthInlineStatus(err instanceof Error ? err.message : 'OAuth Login fehlgeschlagen.', true);
    } finally {
      this.oauthInFlight = false;
    }
  }

  private logoutAccount(): void {
    this.auth.logout();
    this.sm.setAuthToken('');
    this.authAccount = null;
    this.access = null;
    this.profileData = null;
    this.closePremiumModal();
    this.renderAuthState();
    this.setAuthInlineStatus('Abgemeldet.');
    if (this.activeView !== 'main') this.openHomeView();
  }

  private async buyPremiumStripe(): Promise<void> {
    if (!this.authAccount) {
      this.openAuthModal('Bitte zuerst einloggen.', true);
      return;
    }
    try {
      this.setPremiumInlineStatus('Öffne Stripe Checkout...');
      const checkout = await this.auth.createStripeCheckout(this.getBillingReturnToUrl(), this.countryCode);
      this.setPendingBilling('stripe', checkout.sessionId);
      await this.openCheckoutUrl(checkout.checkoutUrl);
    } catch (err) {
      this.setPremiumInlineStatus(err instanceof Error ? err.message : 'Stripe Checkout fehlgeschlagen.', true);
    }
  }

  private async buyPremiumPayPal(): Promise<void> {
    if (!this.authAccount) {
      this.openAuthModal('Bitte zuerst einloggen.', true);
      return;
    }
    try {
      this.setPremiumInlineStatus('Öffne PayPal Checkout...');
      const checkout = await this.auth.createPayPalCheckout(this.getBillingReturnToUrl(), this.countryCode);
      this.setPendingBilling('paypal', checkout.orderId);
      await this.openCheckoutUrl(checkout.checkoutUrl);
    } catch (err) {
      this.setPremiumInlineStatus(err instanceof Error ? err.message : 'PayPal Checkout fehlgeschlagen.', true);
    }
  }

  private getBillingReturnToUrl(): string {
    if (typeof window === 'undefined') return CLIENT_APP_URL;
    if (!this.isNativeMobileApp()) return window.location.href;

    try {
      const returnUrl = new URL(NATIVE_BILLING_RETURN_URL);
      returnUrl.searchParams.set('target', NATIVE_BILLING_REDIRECT_URL);
      return returnUrl.toString();
    } catch {
      const returnUrl = new URL(CLIENT_APP_URL);
      returnUrl.searchParams.set('nativeBillingReturn', NATIVE_BILLING_REDIRECT_URL);
      return returnUrl.toString();
    }
  }

  private async openCheckoutUrl(checkoutUrl: string): Promise<void> {
    const normalized = String(checkoutUrl || '').trim();
    if (!normalized) throw new Error('Checkout-URL fehlt.');

    if (!this.isNativeMobileApp()) {
      window.location.href = normalized;
      return;
    }

    await this.setupNativeBillingHandlers();
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: normalized });
    } catch {
      window.location.href = normalized;
    }
  }

  private async buyPremiumInApp(): Promise<void> {
    if (!this.authAccount) {
      this.openAuthModal('Bitte zuerst einloggen.', true);
      return;
    }

    if (!this.isNativeMobileApp()) {
      this.setPremiumInlineStatus('In-App-Käufe sind hier nicht verfügbar. Nutze Karte oder PayPal.', true);
      return;
    }

    try {
      this.setPremiumInlineStatus('In-App-Kauf wird vorbereitet...');
      const { default: inAppPurchaseManager } = await import('../native/InAppPurchaseManager');
      const access: AccessState = await inAppPurchaseManager.purchasePremium({
        onStatus: (message, isError = false) => this.setPremiumInlineStatus(message, isError),
      });

      this.access = access;
      this.setAuthInlineStatus('In-App-Kauf erfolgreich abgeschlossen.');
      this.setPremiumInlineStatus('Premium wurde freigeschaltet.');
      this.renderAuthState();
    } catch (err) {
      this.setPremiumInlineStatus(err instanceof Error ? err.message : 'In-App-Kauf fehlgeschlagen.', true);
    }
  }

  private async restorePremiumInApp(): Promise<void> {
    if (!this.authAccount) {
      this.openAuthModal('Bitte zuerst einloggen.', true);
      return;
    }

    if (!this.isNativeMobileApp()) {
      this.setPremiumInlineStatus('Käufe können hier nicht wiederhergestellt werden.', true);
      return;
    }

    try {
      this.setPremiumInlineStatus('Wiederherstellung wird vorbereitet...');
      const { default: inAppPurchaseManager } = await import('../native/InAppPurchaseManager');
      const access: AccessState = await inAppPurchaseManager.restorePremium({
        onStatus: (message, isError = false) => this.setPremiumInlineStatus(message, isError),
      });

      this.access = access;
      this.setAuthInlineStatus('Frühere In-App-Käufe wurden wiederhergestellt.');
      this.setPremiumInlineStatus('Premium wurde wiederhergestellt.');
      this.renderAuthState();
    } catch (err) {
      this.setPremiumInlineStatus(err instanceof Error ? err.message : 'Wiederherstellung fehlgeschlagen.', true);
    }
  }

  private isNativeMobileApp(): boolean {
    if (!Capacitor.isNativePlatform()) return false;
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android';
  }

  private getPlatform(): string {
    if (!Capacitor.isNativePlatform()) return 'web';
    return Capacitor.getPlatform();
  }

  private isInAppPurchaseUiPlatform(): boolean {
    return this.isNativeMobileApp();
  }

  private getInAppPurchaseStoreLabel(): string {
    const platform = this.getPlatform();
    if (platform === 'ios') return 'App Store';
    if (platform === 'android') return 'Google Play';
    return 'Store';
  }

  private async handleBillingReturnFromUrl(urlRaw?: string): Promise<void> {
    if (typeof window === 'undefined') return;
    const url = new URL(urlRaw || window.location.href);
    const billing = url.searchParams.get('billing');
    const provider = url.searchParams.get('provider');
    if (!billing) return;

    if (this.redirectWebBillingReturnToNativeApp(url)) return;

    let shouldClearParams = false;

    if (billing === 'paypal-approve') {
      const orderId = url.searchParams.get('token') || '';
      if (orderId) {
        this.setPendingBilling('paypal', orderId);
        try {
          const access = await this.auth.capturePayPal(orderId);
          this.clearPendingBilling();
          if (this.authAccount) this.access = access;
          this.setAuthInlineStatus('PayPal Zahlung abgeschlossen.');
          this.setPremiumInlineStatus('PayPal Zahlung abgeschlossen.');
          shouldClearParams = true;
        } catch (err) {
          this.setAuthInlineStatus(err instanceof Error ? err.message : 'PayPal Capture fehlgeschlagen.', true);
          this.setPremiumInlineStatus(err instanceof Error ? err.message : 'PayPal Capture fehlgeschlagen.', true);
        }
      }
    } else if (billing === 'success' && provider === 'stripe') {
      const rawSessionId = String(url.searchParams.get('stripe_session_id') || '').trim();
      const sessionId = rawSessionId && rawSessionId !== '{CHECKOUT_SESSION_ID}' ? rawSessionId : '';
      if (sessionId) {
        this.setPendingBilling('stripe', sessionId);
        try {
          const access = await this.auth.confirmStripeCheckout(sessionId);
          this.clearPendingBilling();
          if (this.authAccount) this.access = access;
          this.setAuthInlineStatus('Stripe Zahlung abgeschlossen.');
          this.setPremiumInlineStatus('Stripe Zahlung abgeschlossen.');
          shouldClearParams = true;
        } catch (err) {
          this.setAuthInlineStatus(err instanceof Error ? err.message : 'Stripe Bestätigung fehlgeschlagen.', true);
          this.setPremiumInlineStatus(err instanceof Error ? err.message : 'Stripe Bestätigung fehlgeschlagen.', true);
        }
      } else {
        this.setAuthInlineStatus('Zahlung erfolgreich. Status wird aktualisiert...');
        this.setPremiumInlineStatus('Zahlung erfolgreich. Status wird aktualisiert...');
        shouldClearParams = true;
      }
    } else if (billing === 'success') {
      this.setAuthInlineStatus('Zahlung erfolgreich. Status wird aktualisiert...');
      this.setPremiumInlineStatus('Zahlung erfolgreich. Status wird aktualisiert...');
      shouldClearParams = true;
    } else if (billing === 'cancel') {
      this.clearPendingBilling();
      this.setAuthInlineStatus('Zahlung abgebrochen.', true);
      this.setPremiumInlineStatus('Zahlung abgebrochen.', true);
      shouldClearParams = true;
    }

    if (shouldClearParams) {
      url.searchParams.delete('billing');
      url.searchParams.delete('provider');
      url.searchParams.delete('token');
      url.searchParams.delete('PayerID');
      url.searchParams.delete('stripe_session_id');
      if (!urlRaw || urlRaw === window.location.href) {
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
      }
    }
  }

  private redirectWebBillingReturnToNativeApp(url: URL): boolean {
    const nativeReturn = String(url.searchParams.get('nativeBillingReturn') || '').trim();
    if (!nativeReturn || !url.searchParams.get('billing')) return false;

    try {
      const target = new URL(nativeReturn);
      ['billing', 'provider', 'token', 'PayerID', 'stripe_session_id'].forEach((key) => {
        const value = url.searchParams.get(key);
        if (value) target.searchParams.set(key, value);
      });
      window.location.replace(target.toString());
      return true;
    } catch {
      return false;
    }
  }

  private async setupNativeBillingHandlers(): Promise<void> {
    if (!this.isNativeMobileApp() || this.nativeBillingListenerReady) return;

    try {
      const { App } = await import('@capacitor/app');
      this.nativeBillingAppUrlListener = await App.addListener('appUrlOpen', (event) => {
        void this.handleNativeBillingReturnUrl(String(event?.url || '').trim());
      });

      const { Browser } = await import('@capacitor/browser');
      this.nativeBillingBrowserFinishedListener = await Browser.addListener('browserFinished', () => {
        if (!this.getPendingBilling()) return;
        this.setPremiumInlineStatus('Zahlungsfenster geschlossen. Status wird aktualisiert...');
        void this.refreshAuthState();
      });

      const launchUrl = await App.getLaunchUrl();
      void this.handleNativeBillingReturnUrl(String(launchUrl?.url || '').trim());
      this.nativeBillingListenerReady = true;
    } catch {
      this.nativeBillingListenerReady = false;
    }
  }

  private async handleNativeBillingReturnUrl(urlRaw: string): Promise<void> {
    const url = String(urlRaw || '').trim();
    if (!url || url === this.lastHandledNativeBillingUrl) return;

    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return;
    }

    if (parsed.protocol !== `${NATIVE_BILLING_REDIRECT_URL.split(':')[0]}:`) return;
    if (parsed.hostname !== 'billing' || !parsed.searchParams.get('billing')) return;

    this.lastHandledNativeBillingUrl = url;
    await this.closeNativeCheckoutBrowser();
    this.setPremiumInlineStatus('Zahlung wird bestätigt...');
    await this.handleBillingReturnFromUrl(url);
    await this.refreshAuthState();
  }

  private async closeNativeCheckoutBrowser(): Promise<void> {
    if (!this.isNativeMobileApp()) return;
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.close();
    } catch {
      // ignore
    }
  }

  private async retryPendingBilling(): Promise<void> {
    const pending = this.getPendingBilling();
    if (!pending) return;

    try {
      if (pending.provider === 'paypal') {
        const access = await this.auth.capturePayPal(pending.id);
        if (this.authAccount) this.access = access;
        this.setAuthInlineStatus('PayPal Zahlung nachträglich bestätigt.');
        this.setPremiumInlineStatus('PayPal Zahlung nachträglich bestätigt.');
      } else {
        const access = await this.auth.confirmStripeCheckout(pending.id);
        if (this.authAccount) this.access = access;
        this.setAuthInlineStatus('Stripe Zahlung nachträglich bestätigt.');
        this.setPremiumInlineStatus('Stripe Zahlung nachträglich bestätigt.');
      }
      this.clearPendingBilling();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Zahlung konnte noch nicht bestätigt werden.';
      this.setPremiumInlineStatus(message, true);
    }
  }

  private setPendingBilling(provider: 'stripe' | 'paypal', id: string): void {
    const normalizedId = String(id || '').trim();
    if (!normalizedId) return;
    try {
      localStorage.setItem(
        LobbyScene.PENDING_BILLING_KEY,
        JSON.stringify({ provider, id: normalizedId }),
      );
    } catch {
      // ignore
    }
  }

  private getPendingBilling(): { provider: 'stripe' | 'paypal'; id: string } | null {
    try {
      const raw = localStorage.getItem(LobbyScene.PENDING_BILLING_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { provider?: string; id?: string };
      const provider = parsed.provider === 'stripe' || parsed.provider === 'paypal' ? parsed.provider : '';
      const id = String(parsed.id || '').trim();
      if (!provider || !id) return null;
      return { provider, id };
    } catch {
      return null;
    }
  }

  private clearPendingBilling(): void {
    try {
      localStorage.removeItem(LobbyScene.PENDING_BILLING_KEY);
    } catch {
      // ignore
    }
  }

  private ensureMultiplayerAccess(): boolean {
    if (this.authLoading) {
      this.showError('Account wird geladen...');
      return false;
    }
    if (!this.authAccount) {
      this.openAuthModal('Bitte einloggen, um Multiplayer zu spielen.', true);
      return false;
    }
    if (!this.access?.canPlayMultiplayer) {
      this.openPremiumModal();
      return false;
    }
    return true;
  }

  private createRoom(): void {
    if (!this.ensureMultiplayerAccess()) return;
    const name = this.getMultiplayerName();

    const status = document.getElementById('play-status');
    if (status) {
      status.innerText = 'Erstelle Raum...';
      status.style.color = 'var(--accent-cyan)';
    }
    this.sm.getSocket().emit('CREATE_ROOM', { playerName: name }, (res: JoinResponse) => {
      this.handleJoinResponse(res, name);
    });
  }

  private joinRoom(): void {
    if (!this.ensureMultiplayerAccess()) return;
    const name = this.getMultiplayerName();
    const code = this.getRoomValue();
    if (!code) return this.showError('Bitte gib einen Raum-Code ein.');

    const status = document.getElementById('play-status');
    if (status) {
      status.innerText = 'Trete Raum bei...';
      status.style.color = 'var(--accent-cyan)';
    }
    this.sm.getSocket().emit('JOIN_ROOM', { roomId: code, playerName: name }, (res: JoinResponse) => {
      this.handleJoinResponse(res, name);
    });
  }

  private joinRandomRoom(): void {
    if (!this.ensureMultiplayerAccess()) return;
    const name = this.getMultiplayerName();

    const status = document.getElementById('play-status');
    if (status) {
      status.innerText = 'Suche offenen Raum...';
      status.style.color = 'var(--accent-cyan)';
    }
    this.sm.getSocket().emit('JOIN_RANDOM_ROOM', { playerName: name }, (res: JoinResponse) => {
      this.handleJoinResponse(res, name);
    });
  }

  private startOfflineSinglePlayer(): void {
    const name = this.ensureNameValue('Spieler');
    const status = document.getElementById(this.activeView === 'main' ? 'home-status' : 'play-status');
    if (status) {
      status.innerText = 'Starte Offline-Einzelspieler...';
      status.style.color = 'var(--accent-cyan)';
    }

    try {
      const room = this.sm.startLocalSinglePlayer(name);
      this.inRoom = true;
      this.onGameStart(room);
    } catch {
      this.showError('Offline-Spiel konnte nicht gestartet werden.');
    }
  }

  private handleJoinResponse(res: JoinResponse, name: string): void {
    if (res.success && res.playerId && res.roomId) {
      this.sm.saveSession(res.playerId, res.roomId, name);
      this.inRoom = true;
      const status = document.getElementById('play-status');
      if (status) {
        status.innerText = 'Verbunden. Lobby wird geöffnet...';
        status.style.color = 'var(--accent-green)';
      }
      this.showRoomLobby(res.roomId);
      return;
    }
    const message = res.error || 'Verbindung fehlgeschlagen.';
    
    // Bessere Fehlerbehandlung mit spezifischen Nachrichten
    if (/einloggen/i.test(message)) {
      this.openAuthModal(message, true);
      return;
    }
    if (/premium/i.test(message)) {
      this.openPremiumModal(message, true);
      return;
    }
    if (/nicht gefunden|nicht mehr existiert/i.test(message)) {
      const detail = res.error?.includes('gibt es keine') 
        ? 'Dieser Raumcode existiert nicht. Versuche einen anderen oder erstelle einen neuen Raum.'
        : 'Der Raum wurde geschlossen oder ist nicht mehr verfügbar.';
      this.showError(detail);
      return;
    }
    if (/läuft bereits|voll/i.test(message)) {
      this.showError(message);
      return;
    }
    
    this.showError(message);
  }

  /**
   * Wird aufgerufen, wenn die Session verloren geht (z.B. Raum existiert nicht mehr).
   * Kehrt zur Hauptmenü zurück und zeigt dem Spieler eine aussagekräftige Meldung.
   */
  private onSessionLost(error?: string): void {
    this.inRoom = false;
    this.switchView('main');
    setAppRoute({ kind: 'home' });
    
    let message = 'Du wurdest aus dem Raum entfernt.';
    if (error?.includes('existiert nicht mehr')) {
      message = 'Der Raum wurde geschlossen oder existiert nicht mehr.';
    } else if (error?.includes('Reconnect fehlgeschlagen')) {
      message = 'Verbindung zum Raum verloren. Bitte versuche es später erneut.';
    } else if (error?.includes('Client aktiv')) {
      message = 'Du spielst bereits auf einem anderen Gerät. Bitte beende das andere Spiel.';
    }
    
    this.showError(message);
  }

  private showRoomLobby(roomId?: string): void {
    const code = normalizeRoomCode(roomId || this.sm.roomId);
    if (code) setAppRoute({ kind: 'room', roomId: code });
    this.switchView('room');
  }

  private startGame(): void {
    if (!this.ensureMultiplayerAccess()) return;
    this.sm.getSocket().emit('START_GAME');
  }

  private onRoomUpdate(room: RoomState): void {
    const codeDisplay = document.getElementById('room-code-display');
    if (codeDisplay) codeDisplay.innerText = `CODE: ${room.roomId}`;

    if (normalizeRoomCode(this.sm.roomId) !== room.roomId) {
      this.sm.roomId = room.roomId;
    }
    if (this.activeView !== 'room' && this.inRoom) {
      this.showRoomLobby(room.roomId);
    }

    const slotsContainer = document.getElementById('player-slots');
    if (slotsContainer) {
      let html = '';
      for (let i = 0; i < MAX_PLAYERS; i++) {
        if (i < room.players.length) {
          const p = room.players[i];
          const isMe = p.id === this.sm.playerId;
          const isHost = p.id === room.hostId;
          html += `
            <div class="ui-slot active">
              <div class="ui-slot-dot"></div>
              <div class="ui-slot-info">
                <div class="ui-slot-name" style="color: ${isMe ? 'var(--accent-gold)' : 'var(--text-main)'}">${isHost ? '👑 ' : ''}${p.name}${isMe ? ' (Du)' : ''}</div>
                <div class="ui-slot-meta">${isHost ? 'Host' : 'Spieler'}</div>
              </div>
              <div class="ui-slot-index">#${i + 1}</div>
            </div>
          `;
        } else {
          html += `
            <div class="ui-slot">
              <div class="ui-slot-dot"></div>
              <div class="ui-slot-info">
                <div class="ui-slot-name" style="color: var(--text-dim)">Offen</div>
                <div class="ui-slot-meta">Wartet auf Spieler...</div>
              </div>
              <div class="ui-slot-index">#${i + 1}</div>
            </div>
          `;
        }
      }
      slotsContainer.innerHTML = html;
    }

    const status = document.getElementById('room-status');
    if (status) status.innerText = `${room.players.length}/${MAX_PLAYERS} Spieler verbunden`;

    const btn = document.getElementById('btn-start-game') as HTMLButtonElement;
    if (btn) {
      const amHost = this.sm.playerId === room.hostId;
      if (amHost && room.players.length >= 2) {
        btn.innerText = 'SPIEL STARTEN';
        btn.className = 'ui-btn ui-btn-success';
        btn.disabled = false;
      } else if (amHost) {
        btn.innerText = 'WARTE AUF SPIELER...';
        btn.className = 'ui-btn ui-btn-outline';
        btn.disabled = true;
      } else {
        btn.innerText = 'HOST STARTET...';
        btn.className = 'ui-btn ui-btn-outline';
        btn.disabled = true;
      }
    }
  }

  private onGameStart(room: RoomState): void {
    if (this.sm.isLocalMode()) {
      setAppRoute({ kind: 'gameOffline' });
    } else {
      setAppRoute({ kind: 'game', roomId: room.roomId });
    }
    this.cleanup();
    this.scene.start('GameScene', { room });
  }

  private showError(msg: string): void {
    let targetId = 'play-status';
    if (this.activeView === 'main') targetId = 'home-status';
    if (this.activeView === 'profile') targetId = 'profile-status';
    if (this.activeView === 'room') targetId = 'room-status';
    if (this.activeView === 'leaderboard') targetId = 'leaderboard-status';
    
    const status = document.getElementById(targetId);
    if (status) {
      status.innerText = msg;
      status.style.color = 'var(--accent-red)';
    }
  }

  private openPrivacyPolicy(): void {
    this.openExternalUrl(LobbyScene.PRIVACY_POLICY_URL);
  }

  private openExternalUrl(url: string): void {
    if (typeof window === 'undefined') return;
    const normalized = String(url || '').trim();
    if (!normalized) return;

    try {
      const popup = window.open(normalized, '_blank', 'noopener,noreferrer');
      if (popup) return;
    } catch {
      // fallback below
    }

    window.location.href = normalized;
  }

  private renderSettings(): void {
    const list = document.getElementById('settings-list');
    if (!list) return;

    const keys: SettingKey[] = ['sound'];
    list.innerHTML = keys.map(key => {
      const { title, subtitle } = SETTING_LABELS[key];
      const on = this.settingsState[key];
      return `
        <div class="ui-setting-row">
          <div class="ui-setting-info">
            <h4>${title}</h4>
            <p>${subtitle}</p>
          </div>
          <div class="ui-toggle ${on ? 'on' : ''}" data-key="${key}">
            <div class="ui-toggle-knob"></div>
          </div>
        </div>
      `;
    }).join('');

    document.querySelectorAll('.ui-toggle').forEach(el => {
      el.addEventListener('click', (e) => {
        const key = (e.currentTarget as HTMLElement).getAttribute('data-key') as SettingKey;
        playSfx(this, SFX_TAP_KEY, { volume: 0.2 });
        this.toggleSetting(key);
      });
    });
  }

  private toggleSetting(key: SettingKey): void {
    this.settingsState[key] = !this.settingsState[key];
    this.saveSettings();
    this.renderSettings();
    
    const hint = document.getElementById('settings-hint');
    if (hint) {
      hint.innerText = `Gespeichert: ${SETTING_LABELS[key].title} ${this.settingsState[key] ? 'aktiviert' : 'deaktiviert'}.`;
    }
  }

  private loadSettings(): void {
    this.settingsState = { ...DEFAULT_SETTINGS };
    try {
      const raw = localStorage.getItem('pickitup_settings');
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<Record<SettingKey, unknown>>;
      (Object.keys(DEFAULT_SETTINGS) as SettingKey[]).forEach((key) => {
        if (typeof parsed[key] === 'boolean') {
          this.settingsState[key] = parsed[key] as boolean;
        }
      });
    } catch {
      // ignore
    }
  }

  private saveSettings(): void {
    try {
      localStorage.setItem('pickitup_settings', JSON.stringify(this.settingsState));
    } catch {
      // ignore
    }
  }

  private cleanup(): void {
    const uiLayer = document.getElementById('ui-layer');
    if (uiLayer) uiLayer.innerHTML = '';
    
    const socket = this.sm.getSocket();
    socket.off('ROOM_UPDATE');
    socket.off('GAME_START');
    socket.off('LEADERBOARD');
    socket.off('ONLINE_STATS');
    socket.off('ERROR');
    this.stopOnlineStatsPolling();
    window.removeEventListener('popstate', this.popStateHandler);
    void this.nativeBillingAppUrlListener?.remove();
    void this.nativeBillingBrowserFinishedListener?.remove();
    this.nativeBillingAppUrlListener = null;
    this.nativeBillingBrowserFinishedListener = null;
    this.nativeBillingListenerReady = false;
  }

  private leaveRoom(): void {
    this.inRoom = false;
    this.sm.leaveRoom();
    setAppRoute({ kind: 'play' });
    this.switchView('play');
  }

}
